import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { superadminAPI, licenseAPI, aiUsageAPI } from '../api';
import { Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis } from 'recharts';
import {
  Building2, Users, Server, FileWarning, BarChart3,
  Loader2, ToggleLeft, ToggleRight, Trash2, Globe, Cloud, HardDrive,
  RefreshCw, ChevronDown, ChevronRight, UserX, UserCheck, Search, X,
  Wrench, Eye, EyeOff, FileText, MessageSquare, Activity, TrendingUp,
  LogOut, Shield, Plus, Upload, Bot, Pencil, Copy, Key, AlertTriangle, CheckCircle,
  Check
} from 'lucide-react';

export default function SuperAdminPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('analytics');

  if (!user?.is_super_admin) {
    return (
      <div className="h-[100dvh] flex items-center justify-center bg-[#F8F9FA]">
        <div className="text-center">
          <Shield className="w-12 h-12 text-[#D1D5DB] mx-auto mb-4" />
          <h2 className="text-lg font-bold text-[#111827] mb-2">Access Restricted</h2>
          <p className="text-sm text-[#6B7280] mb-4">This area is for Super Administrators only.</p>
          <button onClick={() => navigate('/dashboard')} className="h-9 px-5 bg-[#6590FF] text-white text-sm font-semibold rounded-lg">Go to Dashboard</button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'tenants', label: 'Tenants', icon: Building2 },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'features', label: 'Features', icon: ToggleRight },
    { id: 'tools', label: 'Tools', icon: Wrench },
    { id: 'tool-builder', label: 'Tool Builder', icon: Wrench },
    { id: 'agents', label: 'AI Agents', icon: Shield },
    { id: 'external-agents', label: '3rd-Party Agents', icon: Globe },
    { id: 'licenses', label: 'Licenses', icon: Key },
    { id: 'vector-db', label: 'Vector DB', icon: HardDrive },
    { id: 'llms', label: 'AI Models', icon: Cloud },
    { id: 'usage', label: 'AI Usage', icon: BarChart3 },
    { id: 'deployment', label: 'Deployment', icon: Server },
    { id: 'logs', label: 'Logs', icon: FileWarning },
    { id: 'health', label: 'Health', icon: Activity },
  ];

  return (
    <div className="h-[100dvh] flex flex-col bg-[#F8F9FA]" data-testid="superadmin-page">
      {/* Header */}
      <header className="h-13 border-b border-[#E5E7EB] bg-white flex items-center px-6 gap-4 flex-shrink-0">
        <div className="flex items-center gap-2">
          <img src="/brainshift-logo.png" alt="BrainShift" className="h-7" />
          <div className="h-5 w-px bg-[#E5E7EB]" />
          <span className="text-xs font-bold text-[#6590FF] tracking-[0.12em] uppercase bg-[#6590FF]/5 px-2.5 py-1 rounded-md border border-[#6590FF]/15">Super Admin</span>
        </div>
        <div className="flex-1" />
        <span className="text-xs text-[#6B7280]">{user?.email}</span>
        <button onClick={() => { logout(); navigate('/login'); }} className="p-1.5 hover:bg-[#F3F4F6] rounded-lg" title="Logout">
          <LogOut className="w-4 h-4 text-[#6B7280]" />
        </button>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <nav className="w-52 border-r border-[#E5E7EB] bg-white py-3 px-2 flex-shrink-0 overflow-y-auto">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium mb-0.5 transition-colors ${
                  active ? 'bg-[#6590FF] text-white' : 'text-[#4B5563] hover:bg-[#F3F4F6]'
                }`} data-testid={`sa-tab-${tab.id}`}>
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {activeTab === 'analytics' && <AnalyticsTab />}
          {activeTab === 'tenants' && <TenantsTab />}
          {activeTab === 'users' && <UsersTab />}
          {activeTab === 'features' && <FeaturesTab />}
          {activeTab === 'tools' && <ToolVisibilityTab />}
          {activeTab === 'tool-builder' && <ToolBuilderTab />}
          {activeTab === 'agents' && <AIAgentsTab />}
          {activeTab === 'external-agents' && <ExternalAgentsTab />}
          {activeTab === 'licenses' && <LicensesTab />}
          {activeTab === 'vector-db' && <VectorBackendTab />}
          {activeTab === 'llms' && <LLMsTab />}
          {activeTab === 'usage' && <SuperAdminUsageTab />}
          {activeTab === 'deployment' && <DeploymentTab />}
          {activeTab === 'logs' && <LogsTab />}
          {activeTab === 'health' && <HealthTab />}
        </div>
      </div>
    </div>
  );
}


/* ===== ANALYTICS ===== */
function AnalyticsTab() {
  const [overview, setOverview] = useState(null);
  const [usage, setUsage] = useState(null);
  const [interaction, setInteraction] = useState(null);
  const [content, setContent] = useState(null);
  const [aiPerf, setAiPerf] = useState(null);
  const [enterprise, setEnterprise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [section, setSection] = useState('overview');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Load overview first (fast), then others in background
      const ov = await superadminAPI.getAnalytics();
      setOverview(ov.data);
      setLoading(false);
      // Load remaining in parallel
      const [us, inter, cont, ai, ent] = await Promise.all([
        superadminAPI.getUsageAnalytics().catch(() => ({ data: null })),
        superadminAPI.getInteractionAnalytics().catch(() => ({ data: null })),
        superadminAPI.getContentAnalytics().catch(() => ({ data: null })),
        superadminAPI.getAIPerformanceAnalytics().catch(() => ({ data: null })),
        superadminAPI.getEnterpriseAnalytics().catch(() => ({ data: null })),
      ]);
      setUsage(us.data); setInteraction(inter.data);
      setContent(cont.data); setAiPerf(ai.data); setEnterprise(ent.data);
    } catch (e) { console.error(e); setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const sections = [
    { id: 'overview', label: 'Overview' },
    { id: 'usage', label: 'Usage' },
    { id: 'interaction', label: 'Interaction' },
    { id: 'content', label: 'Content' },
    { id: 'ai', label: 'AI Performance' },
    { id: 'enterprise', label: 'Enterprise' },
  ];

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 text-[#6590FF] animate-spin" /></div>;

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-5" data-testid="sa-analytics-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-[#111827]">Platform Analytics</h2>
        <button onClick={load} className="h-8 px-3 border border-[#E5E7EB] text-xs font-medium text-[#6B7280] hover:bg-[#F3F4F6] rounded-lg flex items-center gap-1.5">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {/* Section Nav */}
      <div className="flex gap-1 bg-[#F3F4F6] p-0.5 rounded-lg overflow-x-auto">
        {sections.map(s => (
          <button key={s.id} onClick={() => setSection(s.id)}
            className={`px-4 py-1.5 text-xs font-semibold rounded-md whitespace-nowrap transition-colors ${section === s.id ? 'bg-white text-[#111827] shadow-sm' : 'text-[#6B7280] hover:text-[#111827]'}`}>
            {s.label}
          </button>
        ))}
      </div>

      {/* OVERVIEW */}
      {section === 'overview' && overview && <>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            { label: 'Users', value: overview.totals.users, icon: Users, color: '#6590FF' },
            { label: 'Tenants', value: overview.totals.tenants, icon: Building2, color: '#7C3AED' },
            { label: 'Workspaces', value: overview.totals.workspaces, icon: Activity, color: '#059669' },
            { label: 'Sources', value: overview.totals.sources, icon: FileText, color: '#0891B2' },
            { label: 'Documents', value: overview.totals.documents, icon: FileText, color: '#D97706' },
            { label: 'Questions', value: overview.totals.questions, icon: MessageSquare, color: '#DC2626' },
          ].map(s => { const Ic = s.icon; return (
            <div key={s.label} className="bg-white border border-[#E5E7EB] rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider">{s.label}</span>
                <Ic className="w-4 h-4" style={{ color: s.color }} />
              </div>
              <p className="text-2xl font-bold text-[#111827]">{(s.value || 0).toLocaleString()}</p>
            </div>
          ); })}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="User Signups" subtitle="Last 30 days" data={overview.signups_30d} color="#6590FF" />
          <ChartCard title="Questions Asked" subtitle="Last 30 days" data={overview.questions_30d} color="#DC2626" />
          <ChartCard title="Documents Uploaded" subtitle="Last 30 days" data={overview.docs_30d} color="#D97706" />
          <ChartCard title="Active Sessions" subtitle="Return visits, last 30 days" data={overview.sessions_30d} color="#059669" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <BarList title="Workspaces per Tenant" items={overview.workspaces_per_tenant} color="#6590FF" />
          <BarList title="Users per Tenant" items={overview.users_per_tenant} color="#7C3AED" />
        </div>
      </>}

      {/* USAGE */}
      {section === 'usage' && usage && <>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="Daily Active Users" subtitle="Last 30 days" data={usage.daily_active_users} color="#6590FF" />
          <div className="bg-white border border-[#E5E7EB] rounded-xl p-5">
            <h3 className="text-sm font-bold text-[#111827] mb-4">Queries per Session</h3>
            <div className="space-y-2">
              {Object.entries(usage.queries_per_session || {}).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between">
                  <span className="text-xs text-[#4B5563]">{k} queries</span>
                  <div className="flex items-center gap-2 w-48">
                    <div className="flex-1 h-2 bg-[#F3F4F6] rounded-full overflow-hidden">
                      <div className="h-full bg-[#6590FF] rounded-full" style={{ width: `${Math.min((v / Math.max(...Object.values(usage.queries_per_session), 1)) * 100, 100)}%` }} />
                    </div>
                    <span className="text-xs font-bold text-[#111827] w-8 text-right">{v}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* File types */}
        {usage.file_types?.length > 0 && (
          <div className="bg-white border border-[#E5E7EB] rounded-xl p-5">
            <h3 className="text-sm font-bold text-[#111827] mb-3">File Types</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {usage.file_types.map(f => (
                <div key={f.type} className="p-3 bg-[#F9FAFB] rounded-lg">
                  <span className="text-[10px] font-bold text-[#9CA3AF] uppercase">{f.type}</span>
                  <p className="text-lg font-bold text-[#111827]">{f.count}</p>
                  <span className="text-[10px] text-[#6B7280]">{f.total_size_mb} MB</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Agent/Tool Usage */}
        {usage.agent_usage?.length > 0 && (
          <div className="bg-white border border-[#E5E7EB] rounded-xl p-5">
            <h3 className="text-sm font-bold text-[#111827] mb-3">Feature & Tool Usage</h3>
            <div className="space-y-2">
              {usage.agent_usage.map(a => (
                <div key={a.type} className="flex items-center justify-between py-1.5">
                  <span className="text-xs text-[#4B5563] flex-1">{a.type}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-bold text-[#111827]">{a.runs} runs</span>
                    <span className="text-[10px] text-[#059669]">{a.completed} ok</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Top users by sessions */}
        {usage.sessions_per_user?.length > 0 && (
          <BarList title="Most Active Users (by sessions)" items={usage.sessions_per_user.map(u => ({ name: `${u.name} (${u.email})`, count: u.sessions }))} color="#6590FF" />
        )}
      </>}

      {/* INTERACTION */}
      {section === 'interaction' && interaction && <>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Avg Queries/Session" value={interaction.avg_queries_per_session} />
          <StatCard label="Avg Response Length" value={`${Math.round((interaction.avg_response_length || 0) / 1)} chars`} />
          <StatCard label="Short Queries (<50c)" value={interaction.query_length?.short || 0} />
          <StatCard label="Long Queries (>200c)" value={interaction.query_length?.long || 0} />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Session Depth */}
          <div className="bg-white border border-[#E5E7EB] rounded-xl p-5">
            <h3 className="text-sm font-bold text-[#111827] mb-1">Follow-up Depth</h3>
            <p className="text-[10px] text-[#9CA3AF] mb-4">How many iterations before users get what they need</p>
            <div className="space-y-3">
              {[
                { label: 'Single query (got it first try)', val: interaction.session_depth?.single_query, color: '#059669' },
                { label: '2-3 follow-ups', val: interaction.session_depth?.short_2_3, color: '#6590FF' },
                { label: '4-5 follow-ups', val: interaction.session_depth?.medium_4_5, color: '#D97706' },
                { label: '6+ deep dive', val: interaction.session_depth?.deep_6_plus, color: '#DC2626' },
              ].map(d => (
                <div key={d.label} className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: d.color }} />
                  <span className="text-xs text-[#4B5563] flex-1">{d.label}</span>
                  <span className="text-xs font-bold text-[#111827]">{d.val || 0}</span>
                </div>
              ))}
            </div>
          </div>
          {/* Hourly Activity */}
          <div className="bg-white border border-[#E5E7EB] rounded-xl p-5">
            <h3 className="text-sm font-bold text-[#111827] mb-4">Activity by Hour</h3>
            <div className="flex items-end gap-[2px] h-24">
              {(interaction.hourly_activity || []).map((h) => {
                const max = Math.max(...(interaction.hourly_activity || []).map(x => x.count), 1);
                const ht = (h.count / max) * 100;
                return (
                  <div key={h.hour} className="flex-1 group relative">
                    <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-[#111827] text-white text-[9px] px-1 py-0.5 rounded hidden group-hover:block z-10">{h.hour}: {h.count}</div>
                    <div className="w-full rounded-t" style={{ height: `${Math.max(ht, 2)}%`, background: '#6590FF', opacity: 0.5 + (ht / 100) * 0.5 }} />
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between mt-1"><span className="text-[9px] text-[#9CA3AF]">00:00</span><span className="text-[9px] text-[#9CA3AF]">23:00</span></div>
          </div>
        </div>
        {/* Day of Week */}
        {interaction.day_of_week?.length > 0 && (
          <div className="bg-white border border-[#E5E7EB] rounded-xl p-5">
            <h3 className="text-sm font-bold text-[#111827] mb-3">Activity by Day of Week</h3>
            <div className="flex gap-2">
              {interaction.day_of_week.map(d => {
                const max = Math.max(...interaction.day_of_week.map(x => x.count), 1);
                return (
                  <div key={d.day} className="flex-1 text-center">
                    <div className="h-20 flex items-end justify-center mb-1">
                      <div className="w-8 rounded-t" style={{ height: `${(d.count / max) * 100}%`, background: '#6590FF', minHeight: 2 }} />
                    </div>
                    <span className="text-[10px] font-semibold text-[#4B5563]">{d.day}</span>
                    <br /><span className="text-[9px] text-[#9CA3AF]">{d.count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </>}

      {/* CONTENT */}
      {section === 'content' && content && <>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Documents" value={content.source_distribution?.documents || 0} />
          <StatCard label="Web Sources" value={content.source_distribution?.web_sources || 0} />
          <StatCard label="Text Sources" value={content.source_distribution?.text_sources || 0} />
          <StatCard label="Total Chunks" value={(content.total_chunks || 0).toLocaleString()} />
        </div>
        <BarList title="Workspaces by Source Count" items={content.workspaces_by_sources} color="#0891B2" />
        {content.recent_documents?.length > 0 && (
          <div className="bg-white border border-[#E5E7EB] rounded-xl p-5">
            <h3 className="text-sm font-bold text-[#111827] mb-3">Recent Documents</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b border-[#E5E7EB] text-[#9CA3AF]">
                  <th className="text-left py-2 font-semibold">Title</th><th className="text-left py-2 font-semibold">Type</th><th className="text-left py-2 font-semibold">Workspace</th><th className="text-right py-2 font-semibold">Chunks</th><th className="text-right py-2 font-semibold">Date</th>
                </tr></thead>
                <tbody>{content.recent_documents.map((d, i) => (
                  <tr key={d.id || `${d.workspace || ''}-${d.title || ''}-${i}`} className="border-b border-[#F3F4F6]">
                    <td className="py-2 text-[#111827] font-medium">{d.title}</td>
                    <td className="py-2 text-[#6B7280]">{d.type}</td>
                    <td className="py-2 text-[#6B7280]">{d.workspace}</td>
                    <td className="py-2 text-right font-bold text-[#111827]">{d.chunks}</td>
                    <td className="py-2 text-right text-[#9CA3AF]">{new Date(d.date).toLocaleDateString()}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        )}
      </>}

      {/* AI PERFORMANCE */}
      {section === 'ai' && aiPerf && <>
        <div className="grid grid-cols-3 gap-3">
          <StatCard label="Citation Rate" value={`${aiPerf.citation_rate?.percentage || 0}%`} sub={`${aiPerf.citation_rate?.with_citations || 0} of ${aiPerf.citation_rate?.total_responses || 0} responses`} />
          <StatCard label="Avg Response" value={`${Math.round((aiPerf.response_stats?.avg_length || 0))} chars`} />
          <StatCard label="Max Response" value={`${Math.round((aiPerf.response_stats?.max_length || 0))} chars`} />
        </div>
        {aiPerf.agent_performance?.length > 0 && (
          <div className="bg-white border border-[#E5E7EB] rounded-xl p-5">
            <h3 className="text-sm font-bold text-[#111827] mb-3">Agent/Tool Performance</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b border-[#E5E7EB] text-[#9CA3AF]">
                  <th className="text-left py-2 font-semibold">Agent/Tool</th><th className="text-right py-2 font-semibold">Total</th><th className="text-right py-2 font-semibold">Success</th><th className="text-right py-2 font-semibold">Failed</th><th className="text-right py-2 font-semibold">Rate</th>
                </tr></thead>
                <tbody>{aiPerf.agent_performance.map((a) => (
                  <tr key={a.type} className="border-b border-[#F3F4F6]">
                    <td className="py-2 font-medium text-[#111827]">{a.type}</td>
                    <td className="py-2 text-right">{a.total}</td>
                    <td className="py-2 text-right text-[#059669]">{a.success}</td>
                    <td className="py-2 text-right text-[#DC2626]">{a.failed}</td>
                    <td className="py-2 text-right">
                      <span className={`font-bold ${a.rate >= 80 ? 'text-[#059669]' : a.rate >= 50 ? 'text-[#D97706]' : 'text-[#DC2626]'}`}>{a.rate}%</span>
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        )}
        <ChartCard title="Daily Agent Runs" subtitle="Last 30 days" data={(aiPerf.runs_daily || []).map(r => ({ day: r.day, count: r.total }))} color="#7C3AED" />
      </>}

      {/* ENTERPRISE */}
      {section === 'enterprise' && enterprise && <>
        {enterprise.tenant_usage?.length > 0 && (
          <div className="bg-white border border-[#E5E7EB] rounded-xl p-5">
            <h3 className="text-sm font-bold text-[#111827] mb-3">Tenant Knowledge Usage</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b border-[#E5E7EB] text-[#9CA3AF]">
                  <th className="text-left py-2 font-semibold">Tenant</th><th className="text-right py-2 font-semibold">Users</th><th className="text-right py-2 font-semibold">Workspaces</th><th className="text-right py-2 font-semibold">Documents</th>
                </tr></thead>
                <tbody>{enterprise.tenant_usage.map((t) => (
                  <tr key={t.tenant} className="border-b border-[#F3F4F6]">
                    <td className="py-2 font-medium text-[#111827]">{t.tenant}</td>
                    <td className="py-2 text-right">{t.users}</td>
                    <td className="py-2 text-right">{t.workspaces}</td>
                    <td className="py-2 text-right font-bold">{t.documents}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        )}
        {enterprise.power_users?.length > 0 && (
          <div className="bg-white border border-[#E5E7EB] rounded-xl p-5">
            <h3 className="text-sm font-bold text-[#111827] mb-3">Power Users</h3>
            <div className="space-y-2">
              {enterprise.power_users.map((u, i) => (
                <div key={`${u.name}-${u.tenant}`} className="flex items-center gap-3 py-1.5">
                  <span className="w-6 h-6 rounded-full bg-[#6590FF]/10 flex items-center justify-center text-[10px] font-bold text-[#6590FF]">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-medium text-[#111827]">{u.name}</span>
                    <span className="text-[10px] text-[#9CA3AF] ml-2">{u.tenant}</span>
                  </div>
                  <span className="text-xs text-[#6B7280]">{u.sessions} sessions</span>
                  <span className="text-xs text-[#6B7280]">{u.workspaces} ws</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <ChartCard title="Tenant Growth" subtitle="New tenants" data={enterprise.tenant_growth} color="#7C3AED" />
      </>}
    </div>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
      <span className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider">{label}</span>
      <p className="text-xl font-bold text-[#111827] mt-1">{value}</p>
      {sub && <span className="text-[10px] text-[#9CA3AF]">{sub}</span>}
    </div>
  );
}

function ChartCard({ title, subtitle, data, color }) {
  if (!data || data.length === 0) return null;
  const maxVal = Math.max(...data.map(d => d.count), 1);
  const total = data.reduce((s, d) => s + d.count, 0);

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-bold text-[#111827]">{title}</h3>
          <span className="text-[10px] text-[#9CA3AF]">{subtitle}</span>
        </div>
        <span className="text-lg font-bold" style={{ color }}>{total.toLocaleString()}</span>
      </div>
      <div className="flex items-end gap-[2px] h-28">
        {data.map((d) => {
          const h = (d.count / maxVal) * 100;
          return (
            <div key={d.day || d.date} className="flex-1 flex flex-col items-center justify-end group relative">
              <div className="absolute -top-5 bg-[#111827] text-white text-[9px] px-1.5 py-0.5 rounded hidden group-hover:block whitespace-nowrap z-10">
                {d.day?.slice(5)}: {d.count}
              </div>
              <div className="w-full rounded-t transition-all group-hover:opacity-80"
                style={{ height: `${Math.max(h, 2)}%`, background: color, opacity: 0.7 + (h / 100) * 0.3 }} />
            </div>
          );
        })}
      </div>
      <div className="flex justify-between mt-1.5">
        <span className="text-[9px] text-[#9CA3AF]">{data[0]?.day?.slice(5)}</span>
        <span className="text-[9px] text-[#9CA3AF]">{data[data.length - 1]?.day?.slice(5)}</span>
      </div>
    </div>
  );
}

function BarList({ title, items, color }) {
  if (!items || items.length === 0) return null;
  const maxVal = Math.max(...items.map(i => i.count), 1);
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl p-5">
      <h3 className="text-sm font-bold text-[#111827] mb-4">{title}</h3>
      <div className="space-y-2.5">
        {items.map((item) => (
          <div key={item.name}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-[#4B5563] truncate flex-1">{item.name}</span>
              <span className="text-xs font-bold text-[#111827] ml-3">{item.count}</span>
            </div>
            <div className="h-1.5 bg-[#F3F4F6] rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${(item.count / maxVal) * 100}%`, background: color }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


/* ===== TENANTS ===== */
function TenantsTab() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedTenant, setSelectedTenant] = useState(null);

  const load = useCallback(async () => {
    try { const res = await superadminAPI.getTenants(); setTenants(res.data || []); } catch (e) { console.error(e); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = tenants.filter(t =>
    !search || t.name.toLowerCase().includes(search.toLowerCase()) || t.admin_emails?.some(e => e.includes(search.toLowerCase()))
  );

  if (selectedTenant) return <TenantDetail tenantId={selectedTenant} onBack={() => setSelectedTenant(null)} onReload={load} />;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-4" data-testid="sa-tenants-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-[#111827]">All Tenants <span className="text-[#9CA3AF] font-normal text-sm">({tenants.length})</span></h2>
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#9CA3AF]" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tenants..."
            className="w-full h-9 pl-9 pr-3 bg-white border border-[#E5E7EB] rounded-lg text-xs text-[#111827] placeholder:text-[#9CA3AF] focus:outline-none focus:border-[#6590FF]" />
        </div>
      </div>
      {loading ? <Loader2 className="w-6 h-6 text-[#6590FF] animate-spin mx-auto mt-8" /> : (
        <div className="space-y-2">
          {filtered.map(t => (
            <button key={t.id} onClick={() => setSelectedTenant(t.id)}
              className="w-full text-left bg-white border border-[#E5E7EB] rounded-xl p-4 flex items-center gap-4 hover:border-[#6590FF] transition-colors" data-testid={`tenant-row-${t.id}`}>
              <div className="w-10 h-10 rounded-lg bg-[#6590FF]/5 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-[#6590FF]" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-[#111827] truncate">{t.name}</h3>
                <div className="flex items-center gap-3 mt-0.5">
                  <span className="text-[10px] text-[#6B7280]">{t.admin_emails?.[0] || 'No admin'}</span>
                  {t.custom_domain && <span className="text-[10px] text-[#6590FF] flex items-center gap-1"><Globe className="w-3 h-3" />{t.custom_domain}</span>}
                </div>
              </div>
              <div className="flex items-center gap-5 text-xs text-[#6B7280]">
                <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{t.user_count}</span>
                <span>{t.workspace_count} ws</span>
                <span>{new Date(t.date_created).toLocaleDateString()}</span>
              </div>
              <ChevronRight className="w-4 h-4 text-[#D1D5DB]" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function TenantDetail({ tenantId, onBack, onReload }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    superadminAPI.getTenantDetail(tenantId).then(r => setData(r.data)).catch(e => console.error(e)).finally(() => setLoading(false));
  }, [tenantId]);

  const toggleTool = async (toolId, currentStatus) => {
    const newEnabled = currentStatus !== 'active';
    await superadminAPI.updateTenantToolConfig(tenantId, toolId, newEnabled);
    const r = await superadminAPI.getTenantDetail(tenantId);
    setData(r.data);
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 text-[#6590FF] animate-spin" /></div>;
  if (!data) return null;
  if (selectedUser) return <UserDetail userId={selectedUser} onBack={() => setSelectedUser(null)} />;

  const { company, users, stats, tools } = data;
  const activeTools = tools.filter(t => t.status === 'active').length;
  const groupedTools = {};
  for (const t of tools) { (groupedTools[t.category] = groupedTools[t.category] || []).push(t); }

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="tenant-detail">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-2 hover:bg-[#F3F4F6] rounded-lg">
          <ChevronRight className="w-4 h-4 text-[#6B7280] rotate-180" />
        </button>
        <div className="w-11 h-11 rounded-xl bg-[#6590FF]/5 flex items-center justify-center">
          <Building2 className="w-6 h-6 text-[#6590FF]" />
        </div>
        <div className="min-w-0 flex-1">
          {editingName ? (
            <div className="flex items-center gap-2">
              <input value={nameDraft} onChange={e => setNameDraft(e.target.value)} autoFocus
                onKeyDown={async (e) => {
                  if (e.key === 'Escape') { setEditingName(false); setNameDraft(''); }
                  if (e.key === 'Enter') {
                    const n = nameDraft.trim();
                    if (!n || n === company.name) { setEditingName(false); return; }
                    setSavingName(true);
                    try {
                      await superadminAPI.renameTenant(tenantId, n);
                      const r = await superadminAPI.getTenantDetail(tenantId);
                      setData(r.data);
                      setEditingName(false);
                      onReload?.();
                    } catch (err) { alert(err.response?.data?.detail || 'Rename failed'); }
                    setSavingName(false);
                  }
                }}
                disabled={savingName}
                className="h-8 px-2 text-base font-bold text-[#111827] border border-[#6590FF] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6590FF]/30 disabled:opacity-50"
                data-testid="tenant-name-edit-input" />
              <button onClick={() => { setEditingName(false); setNameDraft(''); }} className="p-1 text-[#9CA3AF] hover:text-[#DC2626]" data-testid="tenant-name-cancel"><X className="w-4 h-4" /></button>
              <span className="text-[10px] text-[#9CA3AF]">Press Enter to save · Esc to cancel</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 group">
              <h2 className="text-lg font-bold text-[#111827] truncate" data-testid="tenant-name-display">{company.name}</h2>
              <button onClick={() => { setEditingName(true); setNameDraft(company.name); }}
                className="opacity-0 group-hover:opacity-100 p-1 hover:bg-[#F3F4F6] rounded-md transition-opacity"
                title="Rename tenant"
                data-testid="tenant-name-edit-btn">
                <Pencil className="w-3.5 h-3.5 text-[#9CA3AF]" />
              </button>
            </div>
          )}
          <div className="flex items-center gap-3 text-[11px] text-[#6B7280] mt-0.5">
            {company.industry && <span>{company.industry}</span>}
            {company.custom_domain && <span className="text-[#6590FF] flex items-center gap-0.5"><Globe className="w-3 h-3" />{company.custom_domain}</span>}
            <span>Since {new Date(company.date_created).toLocaleDateString()}</span>
          </div>
        </div>
        <div className="flex-1" />
        <button onClick={() => { window.location.href = `/superadmin/tenants/${tenantId}/manage`; }}
          className="h-8 px-3 bg-[#6590FF] text-white text-xs font-semibold rounded-lg hover:bg-[#0040CC] flex items-center gap-1.5" data-testid="manage-as-admin-btn">
          <Shield className="w-3.5 h-3.5" /> Manage as Admin
        </button>
        <button onClick={async () => { if (window.confirm(`Delete "${company.name}"?`)) { await superadminAPI.deleteTenant(tenantId); onReload(); onBack(); }}}
          className="h-8 px-3 border border-[#FCA5A5] text-[#DC2626] text-xs font-medium rounded-lg hover:bg-[#FEE2E2] flex items-center gap-1.5">
          <Trash2 className="w-3.5 h-3.5" /> Delete
        </button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-5 gap-3">
        {[
          { label: 'Users', value: stats.users, icon: Users, color: '#6590FF' },
          { label: 'Workspaces', value: stats.workspaces, icon: Activity, color: '#059669' },
          { label: 'Documents', value: stats.documents, icon: FileText, color: '#D97706' },
          { label: 'Questions', value: stats.questions, icon: MessageSquare, color: '#DC2626' },
          { label: 'Sessions', value: stats.sessions, icon: Activity, color: '#7C3AED' },
        ].map(s => {
          const Ic = s.icon;
          return (
            <div key={s.label} className="bg-white border border-[#E5E7EB] rounded-xl p-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider">{s.label}</span>
                <Ic className="w-3.5 h-3.5" style={{ color: s.color }} />
              </div>
              <p className="text-xl font-bold text-[#111827]">{s.value}</p>
            </div>
          );
        })}
      </div>

      {/* Section Tabs */}
      <div className="flex gap-1 bg-[#F3F4F6] p-0.5 rounded-lg w-fit">
        {[['overview', 'Users'], ['tools', `Tools (${activeTools}/${tools.length})`]].map(([id, label]) => (
          <button key={id} onClick={() => setActiveSection(id)}
            className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-colors ${activeSection === id ? 'bg-white text-[#111827] shadow-sm' : 'text-[#6B7280] hover:text-[#111827]'}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Users Section */}
      {activeSection === 'overview' && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
                <th className="text-left px-4 py-3 font-semibold text-[#4B5563]">User</th>
                <th className="text-left px-4 py-3 font-semibold text-[#4B5563]">Role</th>
                <th className="text-left px-4 py-3 font-semibold text-[#4B5563]">Joined</th>
                <th className="text-center px-4 py-3 font-semibold text-[#4B5563]">Status</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB] cursor-pointer" onClick={() => setSelectedUser(u.id)}>
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-[#111827]">{u.first_name} {u.last_name}</div>
                    <div className="text-[10px] text-[#9CA3AF]">{u.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-md ${
                      u.role === 'Admin' ? 'bg-[#6590FF]/10 text-[#6590FF]' : 'bg-[#F3F4F6] text-[#6B7280]'
                    }`}>{u.role}</span>
                  </td>
                  <td className="px-4 py-3 text-[#9CA3AF]">{new Date(u.date_created).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold ${u.active ? 'text-[#059669]' : 'text-[#DC2626]'}`}>
                      {u.active ? <><UserCheck className="w-3.5 h-3.5" /> Active</> : <><UserX className="w-3.5 h-3.5" /> Disabled</>}
                    </span>
                  </td>
                  <td className="px-4 py-3"><ChevronRight className="w-4 h-4 text-[#D1D5DB]" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Tools Section */}
      {activeSection === 'tools' && (
        <div className="space-y-3">
          {Object.entries(groupedTools).map(([cat, catTools]) => (
            <div key={cat} className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
              <div className="px-5 py-2.5 bg-[#F9FAFB] border-b border-[#E5E7EB]">
                <span className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider">{cat}</span>
              </div>
              <div className="divide-y divide-[#F3F4F6]">
                {catTools.map(t => (
                  <div key={t.id} className="px-5 py-2.5 flex items-center justify-between">
                    <span className="text-sm text-[#111827]">{t.name}</span>
                    {t.status === 'globally_blocked' ? (
                      <span className="text-[10px] font-semibold text-[#9CA3AF] bg-[#F3F4F6] px-2 py-0.5 rounded">Globally Blocked</span>
                    ) : (
                      <button onClick={() => toggleTool(t.id, t.status)} className="flex items-center gap-1.5" data-testid={`td-tool-${t.id}`}>
                        {t.status === 'active' ? (
                          <><Eye className="w-4 h-4 text-[#059669]" /><span className="text-[10px] font-semibold text-[#059669]">Active</span></>
                        ) : (
                          <><EyeOff className="w-4 h-4 text-[#DC2626]" /><span className="text-[10px] font-semibold text-[#DC2626]">Disabled</span></>
                        )}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ===== USERS ===== */
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);

  const load = useCallback(async () => {
    try { const res = await superadminAPI.getUsers(); setUsers(res.data || []); } catch (e) { console.error(e); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = users.filter(u =>
    !search || u.email.toLowerCase().includes(search.toLowerCase()) || u.first_name?.toLowerCase().includes(search.toLowerCase()) || u.company_name?.toLowerCase().includes(search.toLowerCase())
  );

  if (selectedUser) return <UserDetail userId={selectedUser} onBack={() => setSelectedUser(null)} />;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-4" data-testid="sa-users-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-[#111827]">All Users <span className="text-[#9CA3AF] font-normal text-sm">({users.length})</span></h2>
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#9CA3AF]" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search users..."
            className="w-full h-9 pl-9 pr-3 bg-white border border-[#E5E7EB] rounded-lg text-xs text-[#111827] placeholder:text-[#9CA3AF] focus:outline-none focus:border-[#6590FF]" />
        </div>
      </div>
      {loading ? <Loader2 className="w-6 h-6 text-[#6590FF] animate-spin mx-auto mt-8" /> : (
        <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
                <th className="text-left px-4 py-3 font-semibold text-[#4B5563]">User</th>
                <th className="text-left px-4 py-3 font-semibold text-[#4B5563]">Tenant</th>
                <th className="text-left px-4 py-3 font-semibold text-[#4B5563]">Role</th>
                <th className="text-left px-4 py-3 font-semibold text-[#4B5563]">Joined</th>
                <th className="text-center px-4 py-3 font-semibold text-[#4B5563]">Status</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(u => (
                <tr key={u.id} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB] cursor-pointer" onClick={() => setSelectedUser(u.id)} data-testid={`sa-user-${u.id}`}>
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-[#111827]">{u.first_name} {u.last_name}</div>
                    <div className="text-[10px] text-[#9CA3AF]">{u.email}</div>
                  </td>
                  <td className="px-4 py-3 text-[#6B7280]">{u.company_name}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-md ${
                      u.is_super_admin ? 'bg-[#6590FF]/10 text-[#6590FF]' :
                      u.role === 'Admin' ? 'bg-[#059669]/10 text-[#059669]' : 'bg-[#F3F4F6] text-[#6B7280]'
                    }`}>{u.is_super_admin ? 'Super Admin' : u.role}</span>
                  </td>
                  <td className="px-4 py-3 text-[#9CA3AF]">{new Date(u.date_created).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-center">
                    {!u.is_super_admin && (
                      <button onClick={async (e) => { e.stopPropagation(); await superadminAPI.toggleUser(u.id, !u.active); load(); }}
                        className="inline-flex items-center gap-1" data-testid={`toggle-user-${u.id}`}>
                        {u.active ? <UserCheck className="w-4 h-4 text-[#059669]" /> : <UserX className="w-4 h-4 text-[#DC2626]" />}
                        <span className={`text-[10px] font-semibold ${u.active ? 'text-[#059669]' : 'text-[#DC2626]'}`}>{u.active ? 'Active' : 'Disabled'}</span>
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3"><ChevronRight className="w-4 h-4 text-[#D1D5DB]" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


/* ===== FEATURES ===== */

function UserDetail({ userId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    superadminAPI.getUserDetail(userId).then(r => setData(r.data)).catch(e => console.error(e)).finally(() => setLoading(false));
  }, [userId]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 text-[#6590FF] animate-spin" /></div>;
  if (!data) return null;

  const { user, stats, workspaces, questions_30d, sessions_30d } = data;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="user-detail">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-2 hover:bg-[#F3F4F6] rounded-lg">
          <ChevronRight className="w-4 h-4 text-[#6B7280] rotate-180" />
        </button>
        <div className="w-11 h-11 rounded-full bg-[#6590FF]/10 flex items-center justify-center">
          <Users className="w-5 h-5 text-[#6590FF]" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-[#111827]">{user.first_name} {user.last_name}</h2>
          <div className="flex items-center gap-3 text-[11px] text-[#6B7280]">
            <span>{user.email}</span>
            {user.company_name && <span className="px-2 py-0.5 bg-[#F3F4F6] rounded text-[#4B5563]">{user.company_name}</span>}
            <span className={`font-bold uppercase px-2 py-0.5 rounded-md ${
              user.role === 'Admin' ? 'bg-[#6590FF]/10 text-[#6590FF]' : 'bg-[#F3F4F6] text-[#6B7280]'
            }`}>{user.role}</span>
            <span className={`font-semibold ${user.active ? 'text-[#059669]' : 'text-[#DC2626]'}`}>{user.active ? 'Active' : 'Disabled'}</span>
            <span>Joined {new Date(user.date_created).toLocaleDateString()}</span>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Workspaces', value: stats.workspaces, icon: Activity, color: '#6590FF' },
          { label: 'Documents', value: stats.documents, icon: FileText, color: '#D97706' },
          { label: 'Questions', value: stats.questions, icon: MessageSquare, color: '#DC2626' },
          { label: 'Sessions', value: stats.sessions, icon: Activity, color: '#7C3AED' },
        ].map(s => {
          const Ic = s.icon;
          return (
            <div key={s.label} className="bg-white border border-[#E5E7EB] rounded-xl p-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider">{s.label}</span>
                <Ic className="w-3.5 h-3.5" style={{ color: s.color }} />
              </div>
              <p className="text-xl font-bold text-[#111827]">{s.value}</p>
            </div>
          );
        })}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <MiniChart title="Questions Asked" subtitle="Last 30 days" data={questions_30d} color="#DC2626" />
        <MiniChart title="Sessions" subtitle="Last 30 days" data={sessions_30d} color="#7C3AED" />
      </div>

      {/* Workspaces */}
      {workspaces.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-[#111827] mb-3">Workspaces ({workspaces.length})</h3>
          <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden divide-y divide-[#F3F4F6]">
            {workspaces.map(w => (
              <div key={w.id} className="px-5 py-3 flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-[#111827]">{w.name}</span>
                  <span className="text-[10px] text-[#9CA3AF] ml-2">{w.doc_count} docs</span>
                </div>
                <span className="text-[10px] text-[#9CA3AF]">{new Date(w.date_modified).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MiniChart({ title, subtitle, data, color }) {
  if (!data || data.length === 0) return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl p-5">
      <h3 className="text-sm font-bold text-[#111827]">{title}</h3>
      <p className="text-[10px] text-[#9CA3AF] mt-1">No data available</p>
    </div>
  );
  const maxVal = Math.max(...data.map(d => d.count), 1);
  const total = data.reduce((s, d) => s + d.count, 0);
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-bold text-[#111827]">{title}</h3>
          <span className="text-[10px] text-[#9CA3AF]">{subtitle}</span>
        </div>
        <span className="text-lg font-bold" style={{ color }}>{total}</span>
      </div>
      <div className="flex items-end gap-[2px] h-20">
        {data.map((d) => {
          const h = (d.count / maxVal) * 100;
          return (
            <div key={d.day || d.date} className="flex-1 group relative">
              <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-[#111827] text-white text-[9px] px-1.5 py-0.5 rounded hidden group-hover:block whitespace-nowrap z-10">
                {d.day?.slice(5)}: {d.count}
              </div>
              <div className="w-full rounded-t transition-all group-hover:opacity-80"
                style={{ height: `${Math.max(h, 3)}%`, background: color, opacity: 0.7 + (h / 100) * 0.3 }} />
            </div>
          );
        })}
      </div>
      {data.length > 1 && (
        <div className="flex justify-between mt-1">
          <span className="text-[9px] text-[#9CA3AF]">{data[0]?.day?.slice(5)}</span>
          <span className="text-[9px] text-[#9CA3AF]">{data[data.length - 1]?.day?.slice(5)}</span>
        </div>
      )}
    </div>
  );
}


function FeaturesTab() {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [tenantFeatures, setTenantFeatures] = useState([]);
  const [tenantLoading, setTenantLoading] = useState(false);

  const loadGlobal = useCallback(async () => {
    try { const res = await superadminAPI.getFeatureConfig(); setFeatures(res.data?.features || []); } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadGlobal();
    superadminAPI.getTenants().then(r => setTenants(r.data || [])).catch(() => {});
  }, [loadGlobal]);

  const toggleGlobal = async (featureId, currentEnabled) => {
    await superadminAPI.updateFeatureConfig(featureId, !currentEnabled);
    loadGlobal();
  };

  const loadTenantFeatures = async (tenantId) => {
    setSelectedTenant(tenantId);
    if (!tenantId) { setTenantFeatures([]); return; }
    setTenantLoading(true);
    try { const res = await superadminAPI.getTenantFeatureConfig(tenantId); setTenantFeatures(res.data?.features || []); } catch (e) { console.error(e); }
    setTenantLoading(false);
  };

  const toggleTenantFeature = async (featureId, currentEnabled) => {
    await superadminAPI.updateTenantFeatureConfig(selectedTenant, featureId, !currentEnabled);
    loadTenantFeatures(selectedTenant);
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="sa-features-tab">
      <div>
        <h2 className="text-lg font-bold text-[#111827] mb-1">Platform Features</h2>
        <p className="text-sm text-[#6B7280]">Control which features are available across the platform. Disabled features cannot be enabled by tenant admins.</p>
      </div>
      {loading ? <Loader2 className="w-6 h-6 text-[#6590FF] animate-spin mx-auto mt-8" /> : (
        <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden divide-y divide-[#F3F4F6]">
          {features.map(f => (
            <div key={f.id} className="px-5 py-3.5 flex items-center justify-between">
              <div>
                <span className="text-sm font-medium text-[#111827]">{f.name}</span>
                <p className="text-[11px] text-[#9CA3AF] mt-0.5">{f.desc}</p>
              </div>
              <button onClick={() => toggleGlobal(f.id, f.enabled)} className="flex items-center gap-1.5" data-testid={`global-feat-${f.id}`}>
                {f.enabled ? <ToggleRight className="w-7 h-7 text-[#6590FF]" /> : <ToggleLeft className="w-7 h-7 text-[#D1D5DB]" />}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Per-Tenant */}
      <div className="border-t border-[#E5E7EB] pt-6">
        <h3 className="text-base font-bold text-[#111827] mb-1">Per-Tenant Override</h3>
        <p className="text-sm text-[#6B7280] mb-4">Override feature access for a specific tenant.</p>
        <select value={selectedTenant} onChange={e => loadTenantFeatures(e.target.value)}
          className="h-9 px-3 bg-white border border-[#E5E7EB] rounded-lg text-sm text-[#111827] focus:outline-none focus:border-[#6590FF] mb-4 w-80"
          data-testid="tenant-feature-select">
          <option value="">Select a tenant...</option>
          {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>

        {tenantLoading ? <Loader2 className="w-6 h-6 text-[#6590FF] animate-spin mx-auto" /> : selectedTenant && tenantFeatures.length > 0 && (
          <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden divide-y divide-[#F3F4F6]">
            {tenantFeatures.map(f => (
              <div key={f.id} className="px-5 py-3 flex items-center justify-between">
                <div>
                  <span className={`text-sm font-medium ${f.globally_enabled ? 'text-[#111827]' : 'text-[#D1D5DB] line-through'}`}>{f.name}</span>
                  {!f.globally_enabled && <span className="ml-2 text-[9px] bg-[#FEE2E2] text-[#DC2626] px-1.5 py-0.5 rounded font-bold">GLOBALLY OFF</span>}
                </div>
                {f.globally_enabled ? (
                  <button onClick={() => toggleTenantFeature(f.id, f.tenant_enabled)} data-testid={`tenant-feat-${f.id}`}>
                    {f.tenant_enabled ? <ToggleRight className="w-7 h-7 text-[#6590FF]" /> : <ToggleLeft className="w-7 h-7 text-[#D1D5DB]" />}
                  </button>
                ) : <span className="text-[10px] text-[#D1D5DB]">N/A</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


/* ===== TOOL VISIBILITY ===== */
function ToolVisibilityTab() {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [tenantTools, setTenantTools] = useState([]);
  const [tenantLoading, setTenantLoading] = useState(false);

  const loadGlobal = useCallback(async () => {
    try { const res = await superadminAPI.getGlobalToolConfig(); setTools(res.data?.tools || []); } catch (e) { console.error(e); }
    setLoading(false);
  }, []);
  useEffect(() => { loadGlobal(); superadminAPI.getTenants().then(r => setTenants(r.data || [])).catch(() => {}); }, [loadGlobal]);

  const toggleGlobal = async (toolId, blocked) => { await superadminAPI.updateGlobalToolConfig(toolId, !blocked); loadGlobal(); };
  const loadTenantTools = async (id) => { setSelectedTenant(id); if (!id) return setTenantTools([]); setTenantLoading(true); try { const r = await superadminAPI.getTenantToolConfig(id); setTenantTools(r.data?.tools || []); } catch (e) { console.error('Failed to load tenant tool config:', e); } setTenantLoading(false); };
  const toggleTenant = async (toolId, enabled) => { await superadminAPI.updateTenantToolConfig(selectedTenant, toolId, !enabled); loadTenantTools(selectedTenant); };

  const groupBy = (arr) => { const m = {}; for (const t of arr) (m[t.category] = m[t.category] || []).push(t); return Object.entries(m); };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="sa-tools-tab">
      <div>
        <h2 className="text-lg font-bold text-[#111827] mb-1">Tool Visibility</h2>
        <p className="text-sm text-[#6B7280]">Block tools globally or per-tenant. Blocked tools won't appear in the workspace tools panel.</p>
      </div>
      {loading ? <Loader2 className="w-6 h-6 text-[#6590FF] animate-spin mx-auto mt-8" /> : (
        <div className="space-y-3">
          {groupBy(tools).map(([cat, catTools]) => (
            <div key={cat} className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
              <div className="px-5 py-2.5 bg-[#F9FAFB] border-b border-[#E5E7EB]">
                <span className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider">{cat || 'General'}</span>
              </div>
              <div className="divide-y divide-[#F3F4F6]">
                {catTools.map(t => (
                  <div key={t.id} className="px-5 py-2.5 flex items-center justify-between">
                    <span className="text-sm text-[#111827]">{t.name}</span>
                    <button onClick={() => toggleGlobal(t.id, t.blocked)} className="flex items-center gap-1.5" data-testid={`global-tool-${t.id}`}>
                      {t.blocked ? <><EyeOff className="w-4 h-4 text-[#DC2626]" /><span className="text-[10px] font-semibold text-[#DC2626]">Blocked</span></> : <><Eye className="w-4 h-4 text-[#059669]" /><span className="text-[10px] font-semibold text-[#059669]">Active</span></>}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="border-t border-[#E5E7EB] pt-6">
        <h3 className="text-base font-bold text-[#111827] mb-4">Per-Tenant Override</h3>
        <select value={selectedTenant} onChange={e => loadTenantTools(e.target.value)}
          className="h-9 px-3 bg-white border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#6590FF] mb-4 w-80">
          <option value="">Select a tenant...</option>
          {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        {tenantLoading ? <Loader2 className="w-6 h-6 text-[#6590FF] animate-spin mx-auto" /> : selectedTenant && tenantTools.length > 0 && (
          <div className="space-y-3">
            {groupBy(tenantTools).map(([cat, catTools]) => (
              <div key={cat} className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
                <div className="px-5 py-2 bg-[#F9FAFB] border-b border-[#E5E7EB]"><span className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider">{cat}</span></div>
                <div className="divide-y divide-[#F3F4F6]">
                  {catTools.map(t => (
                    <div key={t.id} className="px-5 py-2.5 flex items-center justify-between">
                      <span className="text-sm text-[#111827]">{t.name}</span>
                      <button onClick={() => toggleTenant(t.id, t.enabled)} data-testid={`tenant-tool-${t.id}`}>
                        {t.enabled ? <Eye className="w-4 h-4 text-[#059669]" /> : <EyeOff className="w-4 h-4 text-[#DC2626]" />}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


/* ===== DEPLOYMENT ===== */
function DeploymentTab() {
  const [info, setInfo] = useState({ mode: 'cloud', default_tenant_id: '', default_admin_user_id: '', default_tenant_name: '', default_admin_email: '', sso_enabled: false, sso_provider: '' });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [draft, setDraft] = useState({ mode: 'cloud', default_tenant_id: '', default_admin_user_id: '' });

  const loadInfo = useCallback(async () => {
    setLoadError(null);
    try {
      const [depRes, tenRes] = await Promise.allSettled([
        superadminAPI.getDeploymentMode(),
        superadminAPI.listAllTenantsForLLM(),
      ]);
      if (depRes.status === 'fulfilled') {
        const data = depRes.value.data || {};
        setInfo(data);
        setDraft({
          mode: data.mode || 'cloud',
          default_tenant_id: data.default_tenant_id || '',
          default_admin_user_id: data.default_admin_user_id || '',
        });
      } else {
        setLoadError(depRes.reason?.response?.data?.detail || depRes.reason?.message || 'Failed to load deployment mode.');
      }
      if (tenRes.status === 'fulfilled') {
        setTenants(Array.isArray(tenRes.value.data) ? tenRes.value.data : []);
      }
    } catch (e) {
      setLoadError(e?.message || 'Failed to load deployment configuration.');
    }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { loadInfo(); }, [loadInfo]);

  // Load admins whenever the selected default_tenant_id changes
  useEffect(() => {
    if (draft.default_tenant_id) {
      setAdminsLoading(true);
      superadminAPI.listTenantAdmins(draft.default_tenant_id)
        .then(r => setAdmins(Array.isArray(r.data) ? r.data : []))
        .catch(() => setAdmins([]))
        .finally(() => setAdminsLoading(false));
    } else {
      setAdmins([]);
      setAdminsLoading(false);
    }
  }, [draft.default_tenant_id]);

  const setMode = (m) => {
    if (m === 'cloud') {
      // Switching back to Cloud: clear stale on-prem IDs so the save payload is always clean.
      setDraft({ mode: 'cloud', default_tenant_id: '', default_admin_user_id: '' });
    } else {
      setDraft(d => ({ ...d, mode: m }));
    }
  };

  const save = async () => {
    if (draft.mode === 'on_premise') {
      if (!draft.default_tenant_id) { alert('Please select a default tenant for OnPremise mode.'); return; }
      if (!draft.default_admin_user_id) { alert('Please select a default tenant Admin.'); return; }
    }
    setSaving(true);
    try {
      const payload = draft.mode === 'cloud'
        ? { mode: 'cloud', default_tenant_id: '', default_admin_user_id: '' }
        : draft;
      await superadminAPI.setDeploymentMode(payload);
      await loadInfo();
      alert('Deployment configuration saved.');
    } catch (e) {
      alert(e.response?.data?.detail || e?.message || 'Save failed');
    }
    setSaving(false);
  };

  const isDirty = draft.mode !== info.mode || draft.default_tenant_id !== (info.default_tenant_id || '') || draft.default_admin_user_id !== (info.default_admin_user_id || '');

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid="sa-deployment-tab">
      <div>
        <h2 className="text-lg font-bold text-[#111827]">Deployment Configuration</h2>
        <p className="text-xs text-[#6B7280] mt-0.5">Choose Cloud SaaS or single-tenant On-Premise. Changes affect login flow, role defaults, and Settings visibility.</p>
      </div>
      {loading ? <Loader2 className="w-6 h-6 text-[#6590FF] animate-spin mx-auto mt-8" /> : loadError ? (
        <div className="bg-[#FEE2E2] border border-[#FECACA] rounded-xl p-4 flex items-start gap-3" data-testid="deploy-load-error">
          <AlertCircle className="w-5 h-5 text-[#DC2626] flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-bold text-[#991B1B] mb-1">Could not load deployment configuration</p>
            <p className="text-xs text-[#7F1D1D] leading-relaxed mb-2">{loadError}</p>
            <button onClick={() => { setLoading(true); loadInfo(); }} className="h-8 px-3 bg-[#DC2626] text-white text-xs font-semibold rounded-xl">Retry</button>
          </div>
        </div>
      ) : (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-6 space-y-5">
          {/* Mode toggle */}
          <div>
            <label className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider block mb-2">Mode</label>
            <div className="grid grid-cols-2 gap-3">
              {[
                { id: 'cloud', label: 'Cloud SaaS', icon: Cloud, desc: 'Multi-tenant. Users self-register and get their own tenant.' },
                { id: 'on_premise', label: 'On-Premise', icon: HardDrive, desc: 'Single tenant. SuperAdmin manages users; SSO supported.' },
              ].map(opt => {
                const Ic = opt.icon;
                const active = draft.mode === opt.id;
                return (
                  <button key={opt.id} type="button" onClick={() => setMode(opt.id)}
                    className={`text-left p-4 rounded-xl border-2 transition-colors ${active ? 'border-[#6590FF] bg-[#6590FF]/5' : 'border-[#E5E7EB] hover:border-[#9CA3AF]'}`}
                    data-testid={`mode-${opt.id}`}>
                    <Ic className={`w-6 h-6 mb-2 ${active ? 'text-[#6590FF]' : 'text-[#6B7280]'}`} />
                    <div className={`text-sm font-bold ${active ? 'text-[#6590FF]' : 'text-[#111827]'}`}>{opt.label}</div>
                    <div className="text-[11px] text-[#6B7280] mt-1">{opt.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* OnPremise extras */}
          {draft.mode === 'on_premise' && (
            <div className="border-t border-[#F3F4F6] pt-5 space-y-4" data-testid="onprem-config-block">
              <div>
                <label className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider block mb-1.5">Default tenant *</label>
                <select value={draft.default_tenant_id} onChange={e => setDraft({ ...draft, default_tenant_id: e.target.value, default_admin_user_id: '' })}
                  className="w-full h-10 px-3 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#6590FF]" data-testid="onprem-tenant-select">
                  <option value="">Select tenant...</option>
                  {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                <p className="text-[10px] text-[#6B7280] mt-1">All SSO-authenticated users will be auto-provisioned as Staff in this tenant.</p>
              </div>
              <div>
                <label className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider block mb-1.5">Default tenant Admin *</label>
                <div className="relative">
                  <select value={draft.default_admin_user_id} onChange={e => setDraft({ ...draft, default_admin_user_id: e.target.value })}
                    className="w-full h-10 px-3 pr-10 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#6590FF]" data-testid="onprem-admin-select" disabled={!draft.default_tenant_id || adminsLoading}>
                    <option value="">{adminsLoading ? 'Loading admins...' : (!draft.default_tenant_id ? 'Pick a tenant first' : (admins.length === 0 ? 'No admins found in this tenant' : 'Select admin...'))}</option>
                    {admins.map(a => <option key={a.id} value={a.id}>{a.email}{a.name ? ` — ${a.name}` : ''}</option>)}
                  </select>
                  {adminsLoading && <Loader2 className="w-4 h-4 text-[#6590FF] animate-spin absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" data-testid="onprem-admin-loading" />}
                </div>
                <p className="text-[10px] text-[#6B7280] mt-1">This admin can sign in via local password (separate from SSO) for break-glass access.</p>
              </div>

              {info.mode === 'on_premise' && info.default_tenant_id && (
                <div className={`p-3 rounded-lg border ${info.sso_enabled ? 'bg-[#ECFDF5] border-[#A7F3D0]' : 'bg-[#FEF3C7] border-[#FDE68A]'}`}>
                  <div className="flex items-center gap-2 text-xs">
                    {info.sso_enabled ? <CheckCircle className="w-4 h-4 text-[#059669]" /> : <AlertCircle className="w-4 h-4 text-[#D97706]" />}
                    <span className={info.sso_enabled ? 'text-[#065F46]' : 'text-[#78350F]'}>
                      <strong>SSO:</strong> {info.sso_enabled ? `Enabled (${info.sso_provider || 'OIDC'})` : 'Not configured'}
                    </span>
                    <span className="opacity-60">·</span>
                    <a href={`/superadmin/tenants/${info.default_tenant_id}/manage`} className="text-[#6590FF] hover:underline">Configure SSO →</a>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 border-t border-[#F3F4F6] pt-4">
            {isDirty && <span className="text-[10px] text-[#D97706] mr-auto">Unsaved changes</span>}
            <button onClick={save} disabled={saving || !isDirty}
              className="h-9 px-5 bg-[#6590FF] hover:bg-[#0040CC] disabled:bg-[#9CA3AF] text-white text-xs font-semibold rounded-lg flex items-center gap-2" data-testid="save-deployment-btn">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />} Save Configuration
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


/* ===== ERROR LOGS ===== */
function LogsTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);
  const [expandedSrc, setExpandedSrc] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await superadminAPI.getErrorLogs();
      setLogs(r.data || []);
    } catch (e) {
      console.error('Failed to load error logs:', e);
    }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const clearLogs = async () => {
    if (!window.confirm('Clear all log files? This deletes the contents of:\n\n• backend.err.log\n• backend.out.log\n• frontend.err.log\n• frontend.out.log\n• BKW.ErrorLog DB table\n\nThis action cannot be undone.')) return;
    setClearing(true);
    try {
      const r = await superadminAPI.clearErrorLogs();
      alert(`Cleared ${r.data?.files_cleared?.length || 0} log files.`);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Clear failed');
    }
    setClearing(false);
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-4" data-testid="sa-logs-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-[#111827]">Error Logs</h2>
        <div className="flex items-center gap-2">
          <button onClick={load} className="h-8 px-3 border border-[#E5E7EB] text-xs font-medium text-[#6B7280] hover:bg-[#F3F4F6] rounded-lg flex items-center gap-1.5" data-testid="refresh-logs-btn">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
          <button onClick={clearLogs} disabled={clearing} className="h-8 px-3 border border-[#FCA5A5] text-xs font-medium text-[#DC2626] hover:bg-[#FEE2E2] rounded-lg flex items-center gap-1.5 disabled:opacity-50" data-testid="clear-logs-btn">
            {clearing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />} Clear Logs
          </button>
        </div>
      </div>
      {loading ? <Loader2 className="w-6 h-6 text-[#6590FF] animate-spin mx-auto mt-8" /> : (
        <div className="space-y-3">
          {logs.map((log) => (
            <div key={log.source} className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
              <button onClick={() => setExpandedSrc(expandedSrc === log.source ? null : log.source)}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#F9FAFB] transition-colors">
                {expandedSrc === log.source ? <ChevronDown className="w-3.5 h-3.5 text-[#6B7280]" /> : <ChevronRight className="w-3.5 h-3.5 text-[#6B7280]" />}
                <FileWarning className="w-4 h-4 text-[#D97706]" />
                <span className="text-xs font-semibold text-[#111827]">{log.source}</span>
                <span className="ml-auto text-[10px] text-[#9CA3AF]">{log.lines?.length || 0} lines</span>
              </button>
              {expandedSrc === log.source && (
                <div className="px-4 pb-4 max-h-[400px] overflow-y-auto bg-[#111827] mx-4 mb-4 rounded-lg">
                  <pre className="text-[11px] text-[#A3E635] font-mono leading-relaxed whitespace-pre-wrap break-all p-3">
                    {(log.lines || []).join('\n')}
                  </pre>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ===== HEALTH DASHBOARD ===== */
function HealthTab() {
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autorefresh, setAutorefresh] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await superadminAPI.getHealthMetrics();
      setM(r.data);
    } catch (e) { console.error('health fetch failed:', e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!autorefresh) return undefined;
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [autorefresh, load]);

  if (loading || !m) return <Loader2 className="w-6 h-6 text-[#6590FF] animate-spin mx-auto mt-8" />;

  const fmtUptime = (s) => {
    const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), mn = Math.floor((s % 3600) / 60);
    return d > 0 ? `${d}d ${h}h ${mn}m` : h > 0 ? `${h}h ${mn}m` : `${mn}m ${s % 60}s`;
  };
  const pingColor = !m.db_ping_ms ? '#9CA3AF' : m.db_ping_ms < 100 ? '#059669' : m.db_ping_ms < 500 ? '#D97706' : '#DC2626';
  const errCount = (m.errors_last_1h_db || 0) + (m.errors_last_1h_logs || 0);
  const errColor = errCount === 0 ? '#059669' : errCount < 50 ? '#D97706' : '#DC2626';

  const Stat = ({ label, value, sub, color = '#6590FF' }) => (
    <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
      <div className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider mb-1">{label}</div>
      <div className="text-2xl font-bold" style={{ color }}>{value}</div>
      {sub && <div className="text-[11px] text-[#6B7280] mt-1">{sub}</div>}
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-5" data-testid="sa-health-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-[#111827]">Platform Health</h2>
          <p className="text-xs text-[#6B7280] mt-0.5">Live metrics — backend pool, vector index, error rate, uptime.</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-[11px] text-[#4B5563]">
            <input type="checkbox" checked={autorefresh} onChange={e => setAutorefresh(e.target.checked)} data-testid="health-autorefresh" />
            Auto-refresh (10s)
          </label>
          <button onClick={load} className="h-8 px-3 border border-[#E5E7EB] text-xs font-medium text-[#6B7280] hover:bg-[#F3F4F6] rounded-lg flex items-center gap-1.5" data-testid="health-refresh-btn">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Uptime" value={fmtUptime(m.uptime_seconds || 0)} sub={`PID ${m.process_pid} · Py ${m.python_version}`} />
        <Stat label="DB Ping" value={m.db_ping_ms ? `${m.db_ping_ms} ms` : '—'} sub={m.db_ping_error || 'Round-trip to MS SQL'} color={pingColor} />
        <Stat label="Errors (last 1h)" value={errCount} sub={`${m.errors_last_1h_db || 0} DB · ${m.errors_last_1h_logs || 0} log lines`} color={errColor} />
        <Stat label="Enabled AI Models" value={m.llms?.enabled_count ?? '—'} sub={`${m.llms?.oci_count ?? 0} via OCI GenAI`} />
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-xl p-5">
        <h3 className="text-sm font-bold text-[#111827] mb-3 flex items-center gap-2"><Server className="w-4 h-4 text-[#6590FF]" /> Database Connection Pool</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider mb-1">Read pool (autocommit)</div>
            <div className="text-sm text-[#111827] font-mono">{m.db_read_pool?.available_now ?? 0} / {m.db_read_pool?.size ?? 0} idle</div>
            <div className="text-[10px] text-[#6B7280] mt-0.5">created: {m.db_read_pool?.created ?? 0} · ready: {String(m.db_read_pool?.initialized ?? false)}</div>
          </div>
          <div>
            <div className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider mb-1">Write pool (transactional)</div>
            <div className="text-sm text-[#111827] font-mono">{m.db_write_pool?.available_now ?? 0} / {m.db_write_pool?.size ?? 0} idle</div>
            <div className="text-[10px] text-[#6B7280] mt-0.5">ready: {String(m.db_write_pool?.initialized ?? false)}</div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-xl p-5">
        <h3 className="text-sm font-bold text-[#111827] mb-3 flex items-center gap-2"><HardDrive className="w-4 h-4 text-[#6590FF]" /> Vector Index (FAISS)</h3>
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div>
            <div className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider mb-1">Loaded Workspaces (RAM)</div>
            <div className="text-base font-bold text-[#111827]">{m.faiss?.loaded_workspaces ?? 0}</div>
          </div>
          <div>
            <div className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider mb-1">Total Chunks (RAM)</div>
            <div className="text-base font-bold text-[#111827]">{(m.faiss?.total_chunks ?? 0).toLocaleString()}</div>
          </div>
          <div>
            <div className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider mb-1">Disk Usage</div>
            <div className="text-base font-bold text-[#111827]">{m.faiss_disk?.total_mb ?? 0} MB</div>
            <div className="text-[10px] text-[#6B7280]">{m.faiss_disk?.files ?? 0} files</div>
          </div>
        </div>
        {m.faiss?.top_workspaces?.length > 0 && (
          <div>
            <div className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider mb-2">Top workspaces by chunk count</div>
            <div className="space-y-1">
              {m.faiss.top_workspaces.map(w => (
                <div key={w.workspace_id} className="flex items-center justify-between text-[11px] font-mono text-[#4B5563] py-1 border-b border-[#F3F4F6] last:border-0">
                  <span className="truncate" title={w.workspace_id}>{w.workspace_id.slice(0, 16)}…</span>
                  <span className="font-semibold text-[#111827]">{w.chunks.toLocaleString()} chunks</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="text-[10px] text-[#9CA3AF] text-right font-mono">Snapshot: {new Date(m.timestamp).toLocaleString()}</div>
    </div>
  );
}



/* ===== TOOL BUILDER ===== */
function ToolBuilderTab() {
  const [tools, setTools] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', category: 'Custom', icon: 'wrench', color: '#6590FF', prompt_template: '', code_template: '', tool_type: 'prompt' });
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [t, tpl] = await Promise.all([superadminAPI.listSystemTools(), superadminAPI.getToolTemplates()]);
      setTools(t.data || []); setTemplates(tpl.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const resetForm = () => { setForm({ name: '', description: '', category: 'Custom', icon: 'wrench', color: '#6590FF', prompt_template: '', code_template: '', tool_type: 'prompt' }); setEditingId(null); };

  const save = async () => {
    setCreating(true);
    try {
      if (editingId) await superadminAPI.updateSystemTool(editingId, form);
      else await superadminAPI.createSystemTool(form);
      resetForm(); load();
    } catch (e) { console.error(e); }
    setCreating(false);
  };

  const importTpl = async (tplId) => {
    try { await superadminAPI.importToolTemplate(tplId); load(); } catch (e) { console.error(e); }
  };

  const editTool = (t) => {
    setForm({ name: t.name, description: t.description, category: t.category, icon: t.icon, color: t.color, prompt_template: t.prompt_template, code_template: t.code_template, tool_type: t.tool_type });
    setEditingId(t.id);
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="sa-tool-builder">
      <h2 className="text-lg font-bold text-[#111827]">Tool Builder</h2>

      {/* Templates */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl p-5">
        <h3 className="text-sm font-bold text-[#111827] mb-3">Import from Templates</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {templates.map(tpl => (
            <button key={tpl.id} onClick={() => importTpl(tpl.id)}
              className="text-left p-3 border border-[#E5E7EB] rounded-lg hover:border-[#6590FF] transition-colors">
              <span className="text-xs font-semibold text-[#111827]">{tpl.name}</span>
              <p className="text-[10px] text-[#6B7280] mt-0.5 line-clamp-1">{tpl.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Create/Edit Form */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl p-5">
        <h3 className="text-sm font-bold text-[#111827] mb-4">{editingId ? 'Edit Tool' : 'Create New Tool'}</h3>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Tool name" className="h-9 px-3 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#6590FF]" data-testid="tool-name-input" />
          <input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} placeholder="Category" className="h-9 px-3 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#6590FF]" />
        </div>
        <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Description" className="w-full h-9 px-3 border border-[#E5E7EB] rounded-lg text-sm mb-3 focus:outline-none focus:border-[#6590FF]" />
        <div className="flex gap-2 mb-3">
          <select value={form.tool_type} onChange={e => setForm({ ...form, tool_type: e.target.value })} className="h-9 px-3 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#6590FF]">
            <option value="prompt">Prompt-based</option>
            <option value="code">Code execution</option>
          </select>
          <input value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} type="color" className="h-9 w-12 border border-[#E5E7EB] rounded-lg cursor-pointer" />
        </div>
        <textarea value={form.tool_type === 'prompt' ? form.prompt_template : form.code_template}
          onChange={e => setForm({ ...form, [form.tool_type === 'prompt' ? 'prompt_template' : 'code_template']: e.target.value })}
          placeholder={form.tool_type === 'prompt' ? 'Prompt template (use {content} for document text)...' : 'Python code template...'}
          rows={6} className="w-full px-3 py-2 border border-[#E5E7EB] rounded-lg text-sm font-mono mb-3 focus:outline-none focus:border-[#6590FF] resize-none" data-testid="tool-prompt-input" />
        <div className="flex gap-2">
          <button onClick={save} disabled={!form.name.trim() || creating} className="h-9 px-5 bg-[#6590FF] text-white text-xs font-semibold rounded-lg disabled:bg-[#9CA3AF] flex items-center gap-2" data-testid="save-tool-btn">
            {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} {editingId ? 'Update' : 'Create'}
          </button>
          {editingId && <button onClick={resetForm} className="h-9 px-4 border border-[#E5E7EB] text-xs font-medium rounded-lg">Cancel</button>}
        </div>
      </div>

      {/* Tools List */}
      {loading ? <Loader2 className="w-6 h-6 text-[#6590FF] animate-spin mx-auto" /> : tools.length > 0 && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden divide-y divide-[#F3F4F6]">
          {tools.map(t => (
            <div key={t.id} className="px-5 py-3 flex items-center gap-4">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm" style={{ background: t.color }}>{t.icon === 'wrench' ? '🔧' : '⚡'}</div>
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium text-[#111827]">{t.name}</span>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[10px] text-[#6590FF] bg-[#6590FF]/5 px-1.5 py-0.5 rounded">{t.category}</span>
                  <span className="text-[10px] text-[#6B7280]">{t.tool_type}</span>
                  {t.is_template && <span className="text-[10px] text-[#D97706] bg-[#FEF3C7] px-1.5 py-0.5 rounded">Template</span>}
                </div>
              </div>
              <button onClick={() => editTool(t)} className="p-1.5 hover:bg-[#F3F4F6] rounded-lg"><Pencil className="w-3.5 h-3.5 text-[#6B7280]" /></button>
              <button onClick={async () => { await superadminAPI.deleteSystemTool(t.id); load(); }} className="p-1.5 hover:bg-[#FEE2E2] rounded-lg"><Trash2 className="w-3.5 h-3.5 text-[#DC2626]" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ===== AI AGENTS ===== */
function AIAgentsTab() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: '', description: '', avatar: '🤖', system_prompt: '', model_provider: 'anthropic', model_name: 'claude-sonnet-4-5-20250929',
    enable_memory: false, enable_web_search: false, enable_citations: true, enable_tool_access: false, scope: 'global'
  });

  const load = useCallback(async () => {
    try { const r = await superadminAPI.listAIAgents(); setAgents(r.data || []); } catch (e) { console.error(e); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setCreating(true);
    try { await superadminAPI.createAIAgent(form); setForm({ name: '', description: '', avatar: '🤖', system_prompt: '', model_provider: 'anthropic', model_name: 'claude-sonnet-4-5-20250929', enable_memory: false, enable_web_search: false, enable_citations: true, enable_tool_access: false, scope: 'global' }); load(); } catch (e) { console.error(e); }
    setCreating(false);
  };

  if (selectedAgent) return <AgentDetail agentId={selectedAgent} onBack={() => { setSelectedAgent(null); load(); }} />;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="sa-ai-agents">
      <h2 className="text-lg font-bold text-[#111827]">AI Agents</h2>

      {/* Create */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl p-5">
        <h3 className="text-sm font-bold text-[#111827] mb-4">Create AI Agent</h3>
        <div className="grid grid-cols-3 gap-3 mb-3">
          <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Agent name" className="h-9 px-3 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#6590FF]" data-testid="agent-name-input" />
          <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Description" className="h-9 px-3 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#6590FF]" />
          <input value={form.avatar} onChange={e => setForm({ ...form, avatar: e.target.value })} placeholder="Avatar emoji" className="h-9 px-3 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#6590FF] w-20" />
        </div>
        <textarea value={form.system_prompt} onChange={e => setForm({ ...form, system_prompt: e.target.value })} placeholder="System prompt — define the agent's personality, expertise, and behavior..." rows={4} className="w-full px-3 py-2 border border-[#E5E7EB] rounded-lg text-sm mb-3 focus:outline-none focus:border-[#6590FF] resize-none" data-testid="agent-prompt-input" />
        <div className="flex flex-wrap gap-4 mb-3">
          {[
            { key: 'enable_memory', label: 'Memory' },
            { key: 'enable_web_search', label: 'Web Search' },
            { key: 'enable_citations', label: 'Citations' },
            { key: 'enable_tool_access', label: 'Tool Access' },
          ].map(f => (
            <label key={f.key} className="flex items-center gap-2 text-xs text-[#4B5563] cursor-pointer">
              <input type="checkbox" checked={form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.checked })} className="rounded border-[#D1D5DB] text-[#6590FF] focus:ring-[#6590FF]" />
              {f.label}
            </label>
          ))}
          <select value={form.scope} onChange={e => setForm({ ...form, scope: e.target.value })} className="h-7 px-2 border border-[#E5E7EB] rounded text-xs focus:outline-none focus:border-[#6590FF]">
            <option value="global">Global</option>
            <option value="tenant">Per-Tenant</option>
          </select>
        </div>
        <button onClick={save} disabled={!form.name.trim() || creating} className="h-9 px-5 bg-[#6590FF] text-white text-xs font-semibold rounded-lg disabled:bg-[#9CA3AF] flex items-center gap-2" data-testid="create-agent-btn">
          {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Create Agent
        </button>
      </div>

      {/* List */}
      {loading ? <Loader2 className="w-6 h-6 text-[#6590FF] animate-spin mx-auto" /> : agents.length === 0 ? (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-12 text-center">
          <Bot className="w-10 h-10 text-[#D1D5DB] mx-auto mb-3" />
          <p className="text-sm text-[#6B7280]">No AI agents yet. Create your first one above.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {agents.map(a => (
            <button key={a.id} onClick={() => setSelectedAgent(a.id)}
              className="w-full text-left bg-white border border-[#E5E7EB] rounded-xl p-4 flex items-center gap-4 hover:border-[#6590FF] transition-colors" data-testid={`agent-${a.id}`}>
              <div className="w-10 h-10 rounded-xl bg-[#F3F4F6] flex items-center justify-center text-xl">{a.avatar}</div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-[#111827]">{a.name}</h4>
                <p className="text-[10px] text-[#6B7280] truncate">{a.description || a.system_prompt?.slice(0, 80) || 'No description'}</p>
              </div>
              <div className="flex items-center gap-3 text-[10px] text-[#6B7280]">
                <span>{a.source_count} sources</span>
                <span className={`px-2 py-0.5 rounded ${a.scope === 'global' ? 'bg-[#6590FF]/5 text-[#6590FF]' : 'bg-[#F3F4F6]'}`}>{a.scope}</span>
                {a.enable_memory && <span className="bg-[#059669]/5 text-[#059669] px-1.5 py-0.5 rounded">Memory</span>}
              </div>
              <ChevronRight className="w-4 h-4 text-[#D1D5DB]" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function AgentDetail({ agentId, onBack }) {
  const [agent, setAgent] = useState(null);
  const [sources, setSources] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [allTenants, setAllTenants] = useState([]);
  const [tools, setTools] = useState([]);
  const [availableTools, setAvailableTools] = useState({ builtin: [], custom: [] });
  const [toolPickerOpen, setToolPickerOpen] = useState(false);
  const [toolSearch, setToolSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [textTitle, setTextTitle] = useState('');
  const [textContent, setTextContent] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState('');
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [agentsRes, srcRes, tenRes, allTenRes, toolsRes, availRes] = await Promise.all([
        superadminAPI.listAIAgents(),
        superadminAPI.getAgentSources(agentId),
        superadminAPI.getAgentTenants(agentId),
        superadminAPI.getTenants(),
        superadminAPI.getAgentTools(agentId),
        superadminAPI.getAvailableTools(),
      ]);
      setAgent((agentsRes.data || []).find(a => a.id === agentId));
      setSources(srcRes.data || []);
      setTenants(tenRes.data || []);
      setAllTenants(allTenRes.data || []);
      setTools(toolsRes.data || []);
      setAvailableTools(availRes.data || { builtin: [], custom: [] });
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [agentId]);
  useEffect(() => { load(); }, [load]);

  const addText = async () => {
    if (!textContent.trim()) return;
    try { await superadminAPI.addAgentTextSource(agentId, { title: textTitle || 'Text', content: textContent }); setTextTitle(''); setTextContent(''); load(); } catch (e) { console.error(e); }
  };

  const uploadFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true); setUploadErr('');
    try {
      const fd = new FormData(); fd.append('file', file);
      await superadminAPI.uploadAgentSource(agentId, fd);
      load();
    } catch (err) { console.error(err); setUploadErr(err.response?.data?.detail || err.message || 'Upload failed'); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const toggleTool = async (toolId, toolSource, assigned) => {
    try {
      if (assigned) await superadminAPI.unassignAgentTool(agentId, toolId);
      else await superadminAPI.assignAgentTool(agentId, toolId, toolSource);
      load();
    } catch (e) { console.error(e); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 text-[#6590FF] animate-spin" /></div>;
  if (!agent) return null;

  const assignedIds = new Set(tenants.map(t => t.company_id));
  const assignedToolIds = new Set(tools.map(t => t.tool_id));
  const allToolsFlat = [...(availableTools.builtin || []), ...(availableTools.custom || [])];
  const filteredTools = allToolsFlat.filter(t =>
    !toolSearch || t.name.toLowerCase().includes(toolSearch.toLowerCase()) || (t.description || '').toLowerCase().includes(toolSearch.toLowerCase())
  );
  const assignedToolsDetail = tools.map(t => allToolsFlat.find(at => at.tool_id === t.tool_id) || { tool_id: t.tool_id, name: t.tool_id, category: '-', tool_source: t.tool_source });

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="agent-detail">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-2 hover:bg-[#F3F4F6] rounded-lg"><ChevronRight className="w-4 h-4 text-[#6B7280] rotate-180" /></button>
        <div className="w-11 h-11 rounded-xl bg-[#F3F4F6] flex items-center justify-center text-2xl">{agent.avatar}</div>
        <div className="flex-1">
          <h2 className="text-lg font-bold text-[#111827]">{agent.name}</h2>
          <div className="flex items-center gap-2 text-[10px] text-[#6B7280]">
            <span className="bg-[#6590FF]/5 text-[#6590FF] px-1.5 py-0.5 rounded font-bold">{agent.scope}</span>
            {agent.enable_memory && <span className="bg-[#059669]/5 text-[#059669] px-1.5 py-0.5 rounded">Memory</span>}
            {agent.enable_web_search && <span className="bg-[#0891B2]/5 text-[#0891B2] px-1.5 py-0.5 rounded">Web</span>}
            {agent.enable_citations && <span className="bg-[#D97706]/5 text-[#D97706] px-1.5 py-0.5 rounded">Citations</span>}
          </div>
        </div>
        <button onClick={async () => { await superadminAPI.deleteAIAgent(agentId); onBack(); }}
          className="h-8 px-3 border border-[#FCA5A5] text-[#DC2626] text-xs font-medium rounded-lg hover:bg-[#FEE2E2] flex items-center gap-1.5">
          <Trash2 className="w-3.5 h-3.5" /> Delete
        </button>
      </div>

      {/* System Prompt */}
      {agent.system_prompt && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-5">
          <h3 className="text-sm font-bold text-[#111827] mb-2">System Prompt</h3>
          <p className="text-xs text-[#4B5563] whitespace-pre-wrap bg-[#F9FAFB] p-3 rounded-lg">{agent.system_prompt}</p>
        </div>
      )}

      {/* Knowledge Sources */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl p-5">
        <h3 className="text-sm font-bold text-[#111827] mb-3">Knowledge Sources ({sources.length})</h3>
        <div className="flex gap-2 mb-4">
          <input ref={fileRef} type="file" onChange={uploadFile} className="hidden" accept=".pdf,.docx,.txt,.pptx,.xlsx,.csv,.png,.jpg,.jpeg,.webp,.gif,.bmp,.tiff,.tif" data-testid="agent-file-input" />
          <button onClick={() => fileRef.current?.click()} disabled={uploading}
            className="h-8 px-3 border border-[#E5E7EB] text-xs font-medium text-[#4B5563] rounded-lg hover:bg-[#F3F4F6] flex items-center gap-1.5" data-testid="upload-agent-source">
            {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Upload File (PDF, DOCX, PPTX, XLSX, CSV, TXT, Images)
          </button>
          {uploadErr && <span className="text-[10px] text-[#DC2626] self-center">{uploadErr}</span>}
        </div>
        {/* Text source form */}
        <div className="flex gap-2 mb-4">
          <input value={textTitle} onChange={e => setTextTitle(e.target.value)} placeholder="Title" className="h-8 px-2 border border-[#E5E7EB] rounded-lg text-xs w-32 focus:outline-none focus:border-[#6590FF]" />
          <input value={textContent} onChange={e => setTextContent(e.target.value)} placeholder="Paste text content..." className="h-8 px-2 border border-[#E5E7EB] rounded-lg text-xs flex-1 focus:outline-none focus:border-[#6590FF]" data-testid="agent-text-content" />
          <button onClick={addText} disabled={!textContent.trim()} className="h-8 px-3 bg-[#6590FF] text-white text-xs font-semibold rounded-lg disabled:bg-[#9CA3AF]" data-testid="agent-add-text-btn">Add Text</button>
        </div>
        {sources.length > 0 ? (
          <div className="space-y-1">
            {sources.map(s => (
              <div key={s.id} className="flex items-center justify-between py-2 px-3 bg-[#F9FAFB] rounded-lg">
                <div className="flex items-center gap-2">
                  <FileText className="w-3.5 h-3.5 text-[#6B7280]" />
                  <span className="text-xs text-[#111827]">{s.title}</span>
                  <span className="text-[10px] text-[#9CA3AF]">{s.type} · {s.chunk_count} chunks</span>
                  <span className={`text-[10px] font-semibold ${s.status === 'ready' ? 'text-[#059669]' : s.status === 'error' ? 'text-[#DC2626]' : 'text-[#D97706]'}`}>{s.status}</span>
                </div>
                <button onClick={async () => { await superadminAPI.deleteAgentSource(agentId, s.id); load(); }} className="p-1 hover:bg-[#FEE2E2] rounded"><Trash2 className="w-3 h-3 text-[#DC2626]" /></button>
              </div>
            ))}
          </div>
        ) : <p className="text-xs text-[#9CA3AF]">No sources yet. Upload files or add text to give this agent knowledge.</p>}
      </div>

      {/* Tools Assignment */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl p-5" data-testid="agent-tools-section">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-[#111827]">Assigned Tools ({tools.length})</h3>
          <button onClick={() => setToolPickerOpen(!toolPickerOpen)} className="h-7 px-3 bg-[#6590FF] text-white text-[11px] font-semibold rounded-lg flex items-center gap-1" data-testid="toggle-tool-picker">
            <Plus className="w-3 h-3" /> {toolPickerOpen ? 'Close' : 'Add Tool'}
          </button>
        </div>
        {assignedToolsDetail.length > 0 ? (
          <div className="flex flex-wrap gap-2 mb-3">
            {assignedToolsDetail.map(t => (
              <span key={t.tool_id} className="inline-flex items-center gap-1.5 text-[11px] font-medium text-[#6590FF] bg-[#6590FF]/5 px-2.5 py-1 rounded-lg border border-[#6590FF]/20">
                {t.name}
                <span className="text-[9px] text-[#6B7280]">{t.category}</span>
                <button onClick={() => toggleTool(t.tool_id, t.tool_source || 'builtin', true)} className="hover:text-[#DC2626]"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        ) : <p className="text-xs text-[#9CA3AF] mb-3">No tools assigned. Add tools so this agent can invoke them during chat.</p>}
        {toolPickerOpen && (
          <div className="border-t border-[#E5E7EB] pt-3">
            <input value={toolSearch} onChange={e => setToolSearch(e.target.value)} placeholder="Search tools..." className="h-8 px-3 w-full border border-[#E5E7EB] rounded-lg text-xs mb-3 focus:outline-none focus:border-[#6590FF]" data-testid="tool-search-input" />
            <div className="max-h-64 overflow-y-auto space-y-1">
              {filteredTools.map(t => {
                const assigned = assignedToolIds.has(t.tool_id);
                return (
                  <button key={t.tool_id} onClick={() => toggleTool(t.tool_id, t.tool_source, assigned)}
                    className={`w-full text-left flex items-center justify-between gap-2 py-2 px-3 rounded-lg border ${assigned ? 'bg-[#6590FF]/5 border-[#6590FF]/30' : 'bg-white border-[#E5E7EB] hover:bg-[#F9FAFB]'}`}
                    data-testid={`tool-option-${t.tool_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-[#111827] flex items-center gap-2">
                        {t.name}
                        <span className={`text-[9px] px-1.5 py-0.5 rounded ${t.tool_source === 'custom' ? 'bg-[#7C3AED]/10 text-[#7C3AED]' : 'bg-[#F3F4F6] text-[#6B7280]'}`}>{t.tool_source}</span>
                        <span className="text-[9px] text-[#6B7280]">{t.category}</span>
                      </div>
                      <p className="text-[10px] text-[#6B7280] truncate">{t.description}</p>
                    </div>
                    {assigned ? <X className="w-3.5 h-3.5 text-[#DC2626] flex-shrink-0" /> : <Plus className="w-3.5 h-3.5 text-[#6590FF] flex-shrink-0" />}
                  </button>
                );
              })}
              {filteredTools.length === 0 && <p className="text-[11px] text-[#9CA3AF] text-center py-4">No tools match your search.</p>}
            </div>
          </div>
        )}
      </div>

      {/* Tenant Assignment */}
      {agent.scope === 'tenant' && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-5">
          <h3 className="text-sm font-bold text-[#111827] mb-3">Assigned Tenants ({tenants.length})</h3>
          <div className="flex flex-wrap gap-2 mb-3">
            {tenants.map(t => (
              <span key={t.company_id} className="inline-flex items-center gap-1 text-xs font-medium text-[#6590FF] bg-[#6590FF]/5 px-2.5 py-1 rounded-lg border border-[#6590FF]/20">
                {t.name}
                <button onClick={async () => { await superadminAPI.unassignAgentTenant(agentId, t.company_id); load(); }} className="hover:text-[#DC2626]"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            {allTenants.filter(t => !assignedIds.has(t.id)).map(t => (
              <button key={t.id} onClick={async () => { await superadminAPI.assignAgentTenant(agentId, t.id); load(); }}
                className="text-[10px] font-medium text-[#6B7280] hover:text-[#6590FF] px-2 py-1 border border-dashed border-[#D1D5DB] hover:border-[#6590FF] rounded-lg transition-colors">
                + {t.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}



/* ===== LICENSES ===== */
function LicensesTab() {
  const [licenses, setLicenses] = useState([]);
  const [activeStatus, setActiveStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [showGeneratedToken, setShowGeneratedToken] = useState(null);
  const [installToken, setInstallToken] = useState('');
  const [installError, setInstallError] = useState('');
  const [form, setForm] = useState({
    customer: '', email: '', tier: 'enterprise', duration_days: 365,
    max_users: 50, max_workspaces: 100, notes: ''
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, statusRes] = await Promise.all([
        superadminAPI.listLicenses(),
        licenseAPI.status().catch(() => ({ data: null })),
      ]);
      setLicenses(listRes.data || []);
      setActiveStatus(statusRes.data);
    } catch (e) { console.error('Failed to load licenses:', e); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    if (!form.customer.trim()) return;
    setCreating(true);
    try {
      const res = await superadminAPI.generateLicense(form);
      setShowGeneratedToken(res.data);
      setForm({ customer: '', email: '', tier: 'enterprise', duration_days: 365, max_users: 50, max_workspaces: 100, notes: '' });
      load();
    } catch (e) { console.error('Failed to generate license:', e); alert(e.response?.data?.detail || 'Failed to generate license'); }
    setCreating(false);
  };

  const copyToClipboard = async (text) => {
    // Try modern Clipboard API first
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        alert('Copied to clipboard');
        return;
      }
    } catch (e) { /* fall through to legacy path */ }
    // Legacy fallback: hidden textarea + execCommand('copy')
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.top = '-1000px';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      ta.setSelectionRange(0, text.length);
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      if (ok) { alert('Copied to clipboard'); return; }
    } catch (e) { /* last resort below */ }
    // Last resort: show token in a prompt so the user can copy manually
    window.prompt('Copy the token below (Ctrl/Cmd+C then Enter):', text);
  };

  const downloadLicense = (token, customer) => {
    try {
      const safeName = (customer || 'license').replace(/[^a-z0-9]/gi, '_');
      const blob = new Blob([token + '\n'], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${safeName}_license.lic`;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revoke later to ensure Safari/Firefox finish the download
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      console.error('Download failed:', e);
      // Fallback: open token in new tab via data URL
      const dataUrl = 'data:text/plain;charset=utf-8,' + encodeURIComponent(token);
      window.open(dataUrl, '_blank');
    }
  };

  const installLicense = async () => {
    setInstallError('');
    try {
      await licenseAPI.install(installToken.trim());
      setInstallToken('');
      setShowInstallModal(false);
      load();
    } catch (e) {
      setInstallError(e.response?.data?.detail || 'Invalid license');
    }
  };

  const uninstallLicense = async () => {
    if (!window.confirm('Uninstall the active license? The on-prem instance will fall back to "no license" state.')) return;
    try { await licenseAPI.uninstall(); load(); } catch (e) { console.error(e); }
  };

  const revokeLicense = async (id) => {
    if (!window.confirm('Revoke this license? This cannot be undone.')) return;
    try { await superadminAPI.revokeLicense(id); load(); } catch (e) { console.error(e); }
  };

  const stateBadge = (state) => {
    const map = {
      active: { color: 'bg-[#059669]/10 text-[#059669] border-[#059669]/30', icon: CheckCircle, label: 'Active' },
      grace: { color: 'bg-[#D97706]/10 text-[#D97706] border-[#D97706]/30', icon: AlertTriangle, label: 'Grace Period' },
      expired: { color: 'bg-[#DC2626]/10 text-[#DC2626] border-[#DC2626]/30', icon: AlertTriangle, label: 'Expired' },
      revoked: { color: 'bg-[#6B7280]/10 text-[#6B7280] border-[#6B7280]/30', icon: X, label: 'Revoked' },
      missing: { color: 'bg-[#9CA3AF]/10 text-[#6B7280] border-[#9CA3AF]/30', icon: AlertTriangle, label: 'Not Installed' },
      tampered: { color: 'bg-[#DC2626]/10 text-[#DC2626] border-[#DC2626]/30', icon: AlertTriangle, label: 'Tampered' },
    };
    const cfg = map[state] || map.missing;
    const Icon = cfg.icon;
    return (
      <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${cfg.color}`}>
        <Icon className="w-3 h-3" /> {cfg.label}
      </span>
    );
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="sa-licenses">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-bold text-[#111827] mb-1">Site Licenses</h2>
          <p className="text-sm text-[#6B7280]">Generate signed license keys for on-premise installations. Customers paste them into their install screen.</p>
        </div>
        <button onClick={() => setShowInstallModal(true)} className="h-9 px-4 border border-[#6590FF] text-[#6590FF] text-xs font-semibold rounded-lg hover:bg-[#6590FF]/5 flex items-center gap-2" data-testid="install-license-btn">
          <Upload className="w-3.5 h-3.5" /> Install License (this instance)
        </button>
      </div>

      {/* Active license status banner */}
      {activeStatus && (
        <div className={`rounded-xl p-5 border ${
          activeStatus.state === 'active' ? 'bg-[#059669]/5 border-[#059669]/30' :
          activeStatus.state === 'grace' ? 'bg-[#D97706]/5 border-[#D97706]/30' :
          'bg-[#DC2626]/5 border-[#DC2626]/30'
        }`} data-testid="active-license-banner">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-sm font-bold text-[#111827]">Active License (this instance)</h3>
                {stateBadge(activeStatus.state)}
              </div>
              {activeStatus.installed ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
                  <div>
                    <div className="text-[10px] text-[#6B7280] uppercase tracking-wider">Customer</div>
                    <div className="text-sm font-semibold text-[#111827]">{activeStatus.customer || '—'}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-[#6B7280] uppercase tracking-wider">Tier</div>
                    <div className="text-sm font-semibold text-[#111827] capitalize">{activeStatus.tier || '—'}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-[#6B7280] uppercase tracking-wider">Expires</div>
                    <div className="text-sm font-semibold text-[#111827]">{activeStatus.expires_at ? new Date(activeStatus.expires_at).toLocaleDateString() : '—'}{activeStatus.days_remaining != null && ` (${activeStatus.days_remaining}d)`}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-[#6B7280] uppercase tracking-wider">Usage</div>
                    <div className="text-sm font-semibold text-[#111827]">
                      <span className={activeStatus.users_over_limit ? 'text-[#DC2626]' : ''}>{activeStatus.usage?.users || 0}/{activeStatus.max_users}</span> users · <span className={activeStatus.workspaces_over_limit ? 'text-[#DC2626]' : ''}>{activeStatus.usage?.workspaces || 0}/{activeStatus.max_workspaces}</span> ws
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-[#6B7280] mt-2">{activeStatus.reason || 'No license is currently installed on this instance.'}</p>
              )}
              {activeStatus.reason && activeStatus.installed && <p className="text-xs text-[#D97706] mt-2 font-medium">{activeStatus.reason}</p>}
            </div>
            {activeStatus.installed && (
              <button onClick={uninstallLicense} className="h-8 px-3 border border-[#FCA5A5] text-[#DC2626] text-xs font-medium rounded-lg hover:bg-[#FEE2E2] flex items-center gap-1.5 flex-shrink-0">
                <Trash2 className="w-3 h-3" /> Uninstall
              </button>
            )}
          </div>
        </div>
      )}

      {/* Generate Form */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl p-5">
        <h3 className="text-sm font-bold text-[#111827] mb-4">Generate New License</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
          <input value={form.customer} onChange={e => setForm({ ...form, customer: e.target.value })} placeholder="Customer name *" className="h-9 px-3 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#6590FF]" data-testid="lic-customer" />
          <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="Customer email" className="h-9 px-3 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#6590FF]" />
          <select value={form.tier} onChange={e => setForm({ ...form, tier: e.target.value })} className="h-9 px-3 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#6590FF]" data-testid="lic-tier">
            <option value="basic">Basic</option>
            <option value="pro">Pro</option>
            <option value="enterprise">Enterprise</option>
          </select>
          <div>
            <label className="text-[10px] font-medium text-[#6B7280] uppercase tracking-wider">Duration (days)</label>
            <input type="number" min="1" value={form.duration_days} onChange={e => setForm({ ...form, duration_days: parseInt(e.target.value) || 365 })} className="h-9 px-3 w-full border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#6590FF]" />
          </div>
          <div>
            <label className="text-[10px] font-medium text-[#6B7280] uppercase tracking-wider">Max users</label>
            <input type="number" min="1" value={form.max_users} onChange={e => setForm({ ...form, max_users: parseInt(e.target.value) || 1 })} className="h-9 px-3 w-full border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#6590FF]" />
          </div>
          <div>
            <label className="text-[10px] font-medium text-[#6B7280] uppercase tracking-wider">Max workspaces</label>
            <input type="number" min="1" value={form.max_workspaces} onChange={e => setForm({ ...form, max_workspaces: parseInt(e.target.value) || 1 })} className="h-9 px-3 w-full border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#6590FF]" />
          </div>
        </div>
        <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Internal notes (optional)" className="h-9 px-3 w-full border border-[#E5E7EB] rounded-lg text-sm mb-3 focus:outline-none focus:border-[#6590FF]" />
        <button onClick={generate} disabled={!form.customer.trim() || creating} className="h-9 px-5 bg-[#6590FF] text-white text-xs font-semibold rounded-lg disabled:bg-[#9CA3AF] flex items-center gap-2" data-testid="generate-license-btn">
          {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Key className="w-3.5 h-3.5" />} Generate License
        </button>
      </div>

      {/* List */}
      <div>
        <h3 className="text-sm font-bold text-[#111827] mb-3">Generated Licenses ({licenses.length})</h3>
        {loading ? <Loader2 className="w-6 h-6 text-[#6590FF] animate-spin mx-auto mt-8" /> : licenses.length === 0 ? (
          <div className="bg-white border border-[#E5E7EB] rounded-xl p-12 text-center">
            <Key className="w-10 h-10 text-[#D1D5DB] mx-auto mb-3" />
            <p className="text-sm text-[#6B7280]">No licenses generated yet. Create one above.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {licenses.map(l => (
              <div key={l.id} className="bg-white border border-[#E5E7EB] rounded-xl p-4" data-testid={`license-${l.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-sm font-semibold text-[#111827]">{l.customer}</h4>
                      {stateBadge(l.state)}
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-[#6590FF]/5 text-[#6590FF]">{l.tier}</span>
                    </div>
                    <p className="text-[11px] text-[#6B7280]">
                      {l.email && <>{l.email} · </>}
                      Expires {new Date(l.expires_at).toLocaleDateString()} · {l.max_users} users · {l.max_workspaces} workspaces
                    </p>
                    {l.notes && <p className="text-[10px] text-[#9CA3AF] mt-1 italic">{l.notes}</p>}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={() => copyToClipboard(l.token)} className="h-7 px-2.5 border border-[#E5E7EB] text-[#4B5563] text-[10px] font-medium rounded-lg hover:bg-[#F3F4F6] flex items-center gap-1" title="Copy token">
                      <Copy className="w-3 h-3" /> Copy
                    </button>
                    <button onClick={() => downloadLicense(l.token, l.customer)} className="h-7 px-2.5 border border-[#E5E7EB] text-[#4B5563] text-[10px] font-medium rounded-lg hover:bg-[#F3F4F6] flex items-center gap-1" title="Download .lic file">
                      <Upload className="w-3 h-3 rotate-180" /> .lic
                    </button>
                    {!l.revoked && (
                      <button onClick={() => revokeLicense(l.id)} className="h-7 px-2.5 border border-[#FCA5A5] text-[#DC2626] text-[10px] font-medium rounded-lg hover:bg-[#FEE2E2]" title="Revoke">
                        Revoke
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Install Modal */}
      {showInstallModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowInstallModal(false)}>
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-bold text-[#111827] mb-2">Install License</h3>
            <p className="text-xs text-[#6B7280] mb-4">Paste a license token below. It will become the active license for this on-premise instance.</p>
            <textarea value={installToken} onChange={e => setInstallToken(e.target.value)} rows={6} placeholder="eyJ..." className="w-full px-3 py-2 border border-[#E5E7EB] rounded-lg text-xs font-mono focus:outline-none focus:border-[#6590FF] resize-none mb-3" data-testid="install-token-input" />
            {installError && <p className="text-xs text-[#DC2626] mb-3">{installError}</p>}
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setShowInstallModal(false); setInstallToken(''); setInstallError(''); }} className="h-9 px-4 border border-[#E5E7EB] text-[#4B5563] text-xs font-medium rounded-lg hover:bg-[#F3F4F6]">Cancel</button>
              <button onClick={installLicense} disabled={!installToken.trim()} className="h-9 px-5 bg-[#6590FF] text-white text-xs font-semibold rounded-lg disabled:bg-[#9CA3AF]" data-testid="confirm-install-btn">Install</button>
            </div>
          </div>
        </div>
      )}

      {/* Generated Token Modal */}
      {showGeneratedToken && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowGeneratedToken(null)}>
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle className="w-5 h-5 text-[#059669]" />
              <h3 className="text-base font-bold text-[#111827]">License Generated</h3>
            </div>
            <p className="text-xs text-[#6B7280] mb-3">Send the token below to <strong>{showGeneratedToken.payload?.customer}</strong>. They will paste it into their on-premise install screen.</p>
            <textarea readOnly value={showGeneratedToken.token} rows={6} className="w-full px-3 py-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg text-[11px] font-mono resize-none mb-3" />
            <div className="flex gap-2 justify-end">
              <button onClick={() => copyToClipboard(showGeneratedToken.token)} className="h-9 px-4 border border-[#E5E7EB] text-[#4B5563] text-xs font-medium rounded-lg hover:bg-[#F3F4F6] flex items-center gap-1.5"><Copy className="w-3 h-3" /> Copy Token</button>
              <button onClick={() => downloadLicense(showGeneratedToken.token, showGeneratedToken.payload?.customer || 'license')} className="h-9 px-4 border border-[#E5E7EB] text-[#4B5563] text-xs font-medium rounded-lg hover:bg-[#F3F4F6] flex items-center gap-1.5"><Upload className="w-3 h-3 rotate-180" /> Download .lic</button>
              <button onClick={() => setShowGeneratedToken(null)} className="h-9 px-5 bg-[#6590FF] text-white text-xs font-semibold rounded-lg">Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/* ===== 3RD-PARTY EXTERNAL AGENTS ===== */
function ExternalAgentsTab() {
  const [agents, setAgents] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const emptyForm = { name: '', description: '', avatar: '🔌', endpoint_url: '', auth_type: 'none', auth_token: '', auth_header_name: 'X-API-Key', scope: 'global', timeout_sec: 60 };
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [agRes, tenRes] = await Promise.all([
        superadminAPI.listExternalAgents(),
        superadminAPI.getTenants().catch(() => ({ data: [] })),
      ]);
      setAgents(agRes.data || []);
      setTenants(tenRes.data || []);
    } catch (e) { console.error('Failed to load 3rd-party agents:', e); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const startCreate = () => { setEditing(null); setForm(emptyForm); setShowForm(true); };
  const startEdit = (a) => { setEditing(a.id); setForm({ ...emptyForm, ...a }); setShowForm(true); };
  const cancelForm = () => { setShowForm(false); setEditing(null); setForm(emptyForm); };

  const save = async () => {
    if (!form.name.trim() || !form.endpoint_url.trim()) return;
    setSaving(true);
    try {
      if (editing) await superadminAPI.updateExternalAgent(editing, form);
      else await superadminAPI.createExternalAgent(form);
      cancelForm();
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this 3rd-party agent? Tenants who had access will lose it.')) return;
    try { await superadminAPI.deleteExternalAgent(id); load(); } catch (e) { console.error(e); }
  };

  const testConnection = async (id) => {
    setTesting(id); setTestResult(null);
    try {
      const r = await superadminAPI.testExternalAgent(id, 'Hello, this is a connection test.');
      setTestResult({ id, ok: true, result: r.data?.result });
    } catch (e) {
      setTestResult({ id, ok: false, error: e.response?.data?.detail || e.message });
    }
    setTesting(null);
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="sa-external-agents">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-bold text-[#111827] mb-1">3rd-Party AI Agents</h2>
          <p className="text-sm text-[#6B7280]">Register external agent endpoints. Each must accept <code className="text-[11px] bg-[#F3F4F6] px-1.5 py-0.5 rounded">POST {'{'}query, user_id{'}'}</code> and return <code className="text-[11px] bg-[#F3F4F6] px-1.5 py-0.5 rounded">{'{'}response, metadata{'}'}</code>.</p>
        </div>
        {!showForm && (
          <button onClick={startCreate} className="h-9 px-4 bg-[#6590FF] text-white text-xs font-semibold rounded-lg flex items-center gap-2" data-testid="add-external-agent-btn">
            <Plus className="w-3.5 h-3.5" /> Add Agent
          </button>
        )}
      </div>

      {showForm && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-5">
          <h3 className="text-sm font-bold text-[#111827] mb-4">{editing ? 'Edit' : 'New'} 3rd-Party Agent</h3>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Agent name *" className="h-9 px-3 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#6590FF]" data-testid="ext-name" />
            <input value={form.avatar} onChange={e => setForm({ ...form, avatar: e.target.value })} placeholder="Avatar emoji" className="h-9 px-3 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#6590FF]" />
            <select value={form.scope} onChange={e => setForm({ ...form, scope: e.target.value })} className="h-9 px-3 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#6590FF]">
              <option value="global">Global (all tenants)</option>
              <option value="tenant">Per-Tenant (assign below)</option>
            </select>
          </div>
          <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Description" className="h-9 px-3 w-full border border-[#E5E7EB] rounded-lg text-sm mb-3 focus:outline-none focus:border-[#6590FF]" />
          <input value={form.endpoint_url} onChange={e => setForm({ ...form, endpoint_url: e.target.value })} placeholder="https://your-agent.example.com/query *" className="h-9 px-3 w-full border border-[#E5E7EB] rounded-lg text-sm mb-3 font-mono focus:outline-none focus:border-[#6590FF]" data-testid="ext-url" />

          <div className="border-t border-[#F3F4F6] pt-3 mb-3">
            <label className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider">Authentication</label>
            <div className="grid grid-cols-3 gap-3 mt-2">
              <select value={form.auth_type} onChange={e => setForm({ ...form, auth_type: e.target.value })} className="h-9 px-3 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#6590FF]" data-testid="ext-auth-type">
                <option value="none">No auth</option>
                <option value="bearer">Bearer Token</option>
                <option value="api_key">API Key (custom header)</option>
              </select>
              {form.auth_type === 'api_key' && (
                <input value={form.auth_header_name} onChange={e => setForm({ ...form, auth_header_name: e.target.value })} placeholder="Header name" className="h-9 px-3 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#6590FF]" />
              )}
              {form.auth_type !== 'none' && (
                <input type="password" autoComplete="off" name="ext-token" data-1p-ignore="true" data-lpignore="true" value={form.auth_token} onChange={e => setForm({ ...form, auth_token: e.target.value })} placeholder="Token / key value" className="h-9 px-3 border border-[#E5E7EB] rounded-lg text-sm font-mono focus:outline-none focus:border-[#6590FF]" data-testid="ext-token" />
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 mb-4">
            <label className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider">Timeout (sec)</label>
            <input type="number" min="5" max="300" value={form.timeout_sec} onChange={e => setForm({ ...form, timeout_sec: parseInt(e.target.value) || 60 })} className="h-8 px-2 w-20 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#6590FF]" />
          </div>

          <div className="flex gap-2 justify-end">
            <button onClick={cancelForm} className="h-9 px-4 border border-[#E5E7EB] text-[#4B5563] text-xs font-medium rounded-lg hover:bg-[#F3F4F6]">Cancel</button>
            <button onClick={save} disabled={!form.name.trim() || !form.endpoint_url.trim() || saving} className="h-9 px-5 bg-[#6590FF] text-white text-xs font-semibold rounded-lg disabled:bg-[#9CA3AF] flex items-center gap-2" data-testid="save-ext-agent-btn">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} {editing ? 'Update' : 'Create'}
            </button>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? <Loader2 className="w-6 h-6 text-[#6590FF] animate-spin mx-auto mt-8" /> : agents.length === 0 ? (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-12 text-center">
          <Globe className="w-10 h-10 text-[#D1D5DB] mx-auto mb-3" />
          <p className="text-sm text-[#6B7280]">No 3rd-party agents yet. Add one above.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {agents.map(a => (
            <ExternalAgentRow key={a.id} agent={a} tenants={tenants} onEdit={() => startEdit(a)} onDelete={() => remove(a.id)}
              onTest={() => testConnection(a.id)} testing={testing === a.id}
              testResult={testResult && testResult.id === a.id ? testResult : null}
              onReload={load} />
          ))}
        </div>
      )}
    </div>
  );
}

function ExternalAgentRow({ agent, tenants, onEdit, onDelete, onTest, testing, testResult, onReload }) {
  const [assignedTenants, setAssignedTenants] = useState([]);
  const [expanded, setExpanded] = useState(false);

  const loadAssignments = useCallback(async () => {
    if (agent.scope !== 'tenant') return;
    try { const r = await superadminAPI.getExternalAgentTenants(agent.id); setAssignedTenants(r.data || []); }
    catch (e) { console.error(e); }
  }, [agent.id, agent.scope]);
  useEffect(() => { if (expanded) loadAssignments(); }, [expanded, loadAssignments]);

  const assign = async (companyId) => {
    try { await superadminAPI.assignExternalAgentTenant(agent.id, companyId); loadAssignments(); onReload(); }
    catch (e) { console.error(e); }
  };
  const unassign = async (companyId) => {
    try { await superadminAPI.unassignExternalAgentTenant(agent.id, companyId); loadAssignments(); onReload(); }
    catch (e) { console.error(e); }
  };

  const assignedIds = new Set(assignedTenants.map(t => t.company_id));

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl p-4" data-testid={`ext-agent-${agent.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#F3F4F6] flex items-center justify-center text-xl flex-shrink-0">{agent.avatar || '🔌'}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="text-sm font-semibold text-[#111827]">{agent.name}</h4>
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${agent.scope === 'global' ? 'bg-[#6590FF]/5 text-[#6590FF]' : 'bg-[#7C3AED]/10 text-[#7C3AED]'}`}>{agent.scope}</span>
            <span className="text-[10px] font-medium text-[#6B7280] bg-[#F3F4F6] px-2 py-0.5 rounded">{agent.auth_type === 'none' ? 'no auth' : agent.auth_type}</span>
            {agent.scope === 'tenant' && <span className="text-[10px] text-[#6B7280]">{agent.tenant_count} tenant(s)</span>}
          </div>
          <p className="text-[11px] text-[#6B7280] mb-1">{agent.description || 'No description'}</p>
          <p className="text-[10px] text-[#9CA3AF] font-mono truncate">{agent.endpoint_url}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={onTest} disabled={testing} className="h-7 px-2.5 border border-[#E5E7EB] text-[#4B5563] text-[10px] font-medium rounded-lg hover:bg-[#F3F4F6] flex items-center gap-1" data-testid={`test-${agent.id}`}>
            {testing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Activity className="w-3 h-3" />} Test
          </button>
          <button onClick={() => setExpanded(!expanded)} className="h-7 px-2.5 border border-[#E5E7EB] text-[#4B5563] text-[10px] font-medium rounded-lg hover:bg-[#F3F4F6] flex items-center gap-1">
            {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />} Manage
          </button>
          <button onClick={onEdit} className="h-7 px-2.5 border border-[#E5E7EB] text-[#4B5563] text-[10px] font-medium rounded-lg hover:bg-[#F3F4F6] flex items-center gap-1"><Pencil className="w-3 h-3" /></button>
          <button onClick={onDelete} className="h-7 px-2.5 border border-[#FCA5A5] text-[#DC2626] text-[10px] font-medium rounded-lg hover:bg-[#FEE2E2] flex items-center gap-1"><Trash2 className="w-3 h-3" /></button>
        </div>
      </div>

      {testResult && (
        <div className={`mt-3 p-3 rounded-lg border text-[11px] ${testResult.ok ? 'bg-[#059669]/5 border-[#059669]/30 text-[#059669]' : 'bg-[#DC2626]/5 border-[#DC2626]/30 text-[#DC2626]'}`}>
          <div className="font-bold mb-1">{testResult.ok ? '✓ Connection OK' : '✗ Connection failed'}</div>
          {testResult.ok ? (
            <pre className="text-[10px] text-[#4B5563] whitespace-pre-wrap font-mono max-h-32 overflow-y-auto">{JSON.stringify(testResult.result, null, 2).slice(0, 800)}</pre>
          ) : (
            <p className="text-[10px]">{testResult.error}</p>
          )}
        </div>
      )}

      {expanded && agent.scope === 'tenant' && (
        <div className="mt-3 pt-3 border-t border-[#F3F4F6]">
          <h5 className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider mb-2">Tenant Assignments ({assignedTenants.length})</h5>
          <div className="flex flex-wrap gap-2 mb-3">
            {assignedTenants.map(t => (
              <span key={t.company_id} className="inline-flex items-center gap-1 text-[11px] font-medium text-[#6590FF] bg-[#6590FF]/5 px-2.5 py-1 rounded-lg border border-[#6590FF]/20">
                {t.name}
                <button onClick={() => unassign(t.company_id)} className="hover:text-[#DC2626]"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {(() => {
              const unassigned = tenants.filter(t => !assignedIds.has(t.id));
              return (
                <>
                  {unassigned.map(t => (
                    <button key={t.id} onClick={() => assign(t.id)}
                      className="text-[10px] font-medium text-[#6B7280] hover:text-[#6590FF] px-2 py-1 border border-dashed border-[#D1D5DB] hover:border-[#6590FF] rounded-lg transition-colors">
                      + {t.name}
                    </button>
                  ))}
                  {unassigned.length === 0 && <p className="text-[10px] text-[#9CA3AF]">All tenants assigned.</p>}
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}



/* ===== VECTOR DB BACKEND ===== */
function VectorBackendTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);
  const [migrate, setMigrate] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [busyAction, setBusyAction] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await superadminAPI.getVectorBackend();
      setData(r.data);
      setDraft(JSON.parse(JSON.stringify(r.data.config)));
    } catch (e) { console.error('Failed to load vector backend config:', e); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading || !data || !draft) {
    return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 text-[#6590FF] animate-spin" /></div>;
  }

  const supported = data.supported_backends || {};
  const currentBackend = data.config?.backend || 'faiss_memory';
  const draftBackendInfo = supported[draft.backend] || {};
  const isChanged = JSON.stringify(data.config) !== JSON.stringify(draft);
  const requiresMigration = draft.backend !== currentBackend;

  const updateNested = (key, field, value) => setDraft({ ...draft, [key]: { ...draft[key], [field]: value } });

  const test = async () => {
    setTesting(true); setTestResult(null);
    try {
      const cfg = draft[draft.backend] || {};
      const r = await superadminAPI.testVectorBackend(draft.backend, cfg);
      setTestResult(r.data);
    } catch (e) {
      setTestResult({ ok: false, error: e.response?.data?.detail || e.message });
    }
    setTesting(false);
  };

  const save = async () => {
    if (!supported[draft.backend]?.enabled) {
      alert(`${supported[draft.backend]?.name || draft.backend} is not enabled. Contact support.`);
      return;
    }
    setSaving(true);
    try {
      const r = await superadminAPI.updateVectorBackend({ ...draft, migrate_existing: requiresMigration && migrate });
      const mig = r.data?.migration;
      if (mig) alert(`Saved. Migration: rebuilt ${mig.rebuilt}/${mig.total_workspaces} workspaces${mig.failed ? `, ${mig.failed} failed` : ''}.`);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const saveNow = async () => {
    setBusyAction('save'); setTestResult(null);
    try {
      const r = await superadminAPI.saveVectorIndicesNow();
      setTestResult(r.data?.ok ? { ok: true, message: `Saved ${r.data.saved} workspace indices to disk.` } : { ok: false, error: r.data?.error });
      load();
    } catch (e) {
      setTestResult({ ok: false, error: e.response?.data?.detail || e.message });
    }
    setBusyAction('');
  };

  const reMigrate = async () => {
    if (!window.confirm('Re-index all workspaces from BKW.DocumentChunks? This may take a while.')) return;
    setBusyAction('migrate'); setTestResult(null);
    try {
      const r = await superadminAPI.migrateVectorBackend();
      setTestResult({ ok: true, message: `Rebuilt ${r.data.rebuilt}/${r.data.total_workspaces} workspaces.` });
      load();
    } catch (e) {
      setTestResult({ ok: false, error: e.response?.data?.detail || e.message });
    }
    setBusyAction('');
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="sa-vector-db">
      <div>
        <h2 className="text-lg font-bold text-[#111827] mb-1">Vector Database</h2>
        <p className="text-sm text-[#6B7280]">Choose where document embeddings are stored. Switching backends rebuilds indices from your existing document chunks.</p>
      </div>

      {/* Status cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
          <div className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider mb-1">Active Backend</div>
          <div className="text-sm font-semibold text-[#111827]">{supported[currentBackend]?.name || currentBackend}</div>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
          <div className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider mb-1">Loaded Workspaces (RAM)</div>
          <div className="text-sm font-semibold text-[#111827]">{data.loaded_workspaces}</div>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
          <div className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider mb-1">Persisted on Disk</div>
          <div className="text-sm font-semibold text-[#111827]">{data.persisted_workspaces}</div>
        </div>
      </div>

      {/* Backend picker */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl p-5">
        <h3 className="text-sm font-bold text-[#111827] mb-3">Select Backend</h3>
        <div className="grid grid-cols-2 gap-3">
          {Object.entries(supported).map(([id, info]) => {
            const isSelected = draft.backend === id;
            const isDisabled = !info.enabled;
            return (
              <button key={id} onClick={() => !isDisabled && setDraft({ ...draft, backend: id })}
                disabled={isDisabled}
                className={`text-left p-4 rounded-lg border-2 transition-colors ${
                  isSelected ? 'border-[#6590FF] bg-[#6590FF]/5' :
                  isDisabled ? 'border-[#E5E7EB] bg-[#F9FAFB] opacity-60 cursor-not-allowed' :
                  'border-[#E5E7EB] bg-white hover:border-[#6590FF]/50 cursor-pointer'
                }`}
                data-testid={`vector-backend-${id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-semibold text-[#111827]">{info.name}</span>
                  {currentBackend === id && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#059669]/10 text-[#059669]">Active</span>}
                  {isDisabled && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#9CA3AF]/10 text-[#6B7280]">Coming Soon</span>}
                </div>
                <p className="text-[11px] text-[#6B7280]">{info.description}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Backend-specific config */}
      {(draft.backend === 'faiss_memory' || draft.backend === 'faiss_disk') && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-5">
          <h3 className="text-sm font-bold text-[#111827] mb-3">FAISS Configuration</h3>
          {draft.backend === 'faiss_disk' && (
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider">Save interval (sec)</label>
                <input type="number" min="30" max="3600" value={draft.save_interval_sec} onChange={e => setDraft({ ...draft, save_interval_sec: parseInt(e.target.value) || 300 })}
                  className="h-9 px-3 w-full border border-[#E5E7EB] rounded-lg text-sm mt-1 focus:outline-none focus:border-[#6590FF]" data-testid="save-interval-input" />
                <p className="text-[10px] text-[#6B7280] mt-1">How often to save in-memory indices to disk. Min 30s.</p>
              </div>
              <div>
                <label className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider">Storage path</label>
                <div className="h-9 px-3 w-full bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg text-xs font-mono text-[#4B5563] flex items-center mt-1">{data.vector_data_dir}</div>
                <p className="text-[10px] text-[#6B7280] mt-1">Override via env var <code>VECTOR_DATA_DIR</code>.</p>
              </div>
            </div>
          )}
          {draft.backend === 'faiss_disk' && currentBackend === 'faiss_disk' && (
            <div className="flex gap-2 mt-2">
              <button onClick={saveNow} disabled={busyAction === 'save'} className="h-8 px-3 border border-[#E5E7EB] text-[#4B5563] text-xs font-medium rounded-lg hover:bg-[#F3F4F6] flex items-center gap-1.5" data-testid="save-now-btn">
                {busyAction === 'save' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Save Now
              </button>
              <button onClick={reMigrate} disabled={busyAction === 'migrate'} className="h-8 px-3 border border-[#E5E7EB] text-[#4B5563] text-xs font-medium rounded-lg hover:bg-[#F3F4F6] flex items-center gap-1.5" data-testid="rebuild-btn">
                {busyAction === 'migrate' ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Rebuild from DB
              </button>
            </div>
          )}
        </div>
      )}

      {draft.backend === 'qdrant' && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-5" data-testid="qdrant-config">
          <h3 className="text-sm font-bold text-[#111827] mb-3">Qdrant Configuration</h3>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <input value={draft.qdrant?.host || ''} onChange={e => updateNested('qdrant', 'host', e.target.value)} placeholder="Host (e.g. localhost or qdrant.example.com)" className="h-9 px-3 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#6590FF]" />
            <input type="number" value={draft.qdrant?.port || 6333} onChange={e => updateNested('qdrant', 'port', parseInt(e.target.value) || 6333)} placeholder="Port" className="h-9 px-3 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#6590FF]" />
            <input type="password" autoComplete="off" name="qdrant-api-key" data-1p-ignore="true" data-lpignore="true" value={draft.qdrant?.api_key || ''} onChange={e => updateNested('qdrant', 'api_key', e.target.value)} placeholder="API Key (optional)" className="h-9 px-3 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#6590FF]" />
            <input value={draft.qdrant?.collection_prefix || 'bkw_'} onChange={e => updateNested('qdrant', 'collection_prefix', e.target.value)} placeholder="Collection prefix" className="h-9 px-3 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#6590FF]" />
          </div>
          <label className="flex items-center gap-2 text-xs text-[#4B5563]">
            <input type="checkbox" checked={!!draft.qdrant?.https} onChange={e => updateNested('qdrant', 'https', e.target.checked)} /> Use HTTPS
          </label>
          <p className="text-[10px] text-[#6B7280] mt-2">Env override: <code>QDRANT_HOST</code>, <code>QDRANT_PORT</code>, <code>QDRANT_API_KEY</code>. UI values take precedence when set.</p>
        </div>
      )}

      {draft.backend === 'pgvector' && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-5" data-testid="pgvector-config">
          <h3 className="text-sm font-bold text-[#111827] mb-3">Postgres pgvector Configuration</h3>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <input value={draft.pgvector?.host || ''} onChange={e => updateNested('pgvector', 'host', e.target.value)} placeholder="Host" className="h-9 px-3 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#6590FF]" />
            <input type="number" value={draft.pgvector?.port || 5432} onChange={e => updateNested('pgvector', 'port', parseInt(e.target.value) || 5432)} placeholder="Port" className="h-9 px-3 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#6590FF]" />
            <input value={draft.pgvector?.database || ''} onChange={e => updateNested('pgvector', 'database', e.target.value)} placeholder="Database name" className="h-9 px-3 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#6590FF]" />
            <input value={draft.pgvector?.user || ''} onChange={e => updateNested('pgvector', 'user', e.target.value)} placeholder="User" className="h-9 px-3 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#6590FF]" />
            <input type="password" autoComplete="off" name="pgvector-password" data-1p-ignore="true" data-lpignore="true" value={draft.pgvector?.password || ''} onChange={e => updateNested('pgvector', 'password', e.target.value)} placeholder="Password" className="h-9 px-3 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#6590FF]" />
            <input value={draft.pgvector?.table_prefix || 'bkw_vec_'} onChange={e => updateNested('pgvector', 'table_prefix', e.target.value)} placeholder="Table prefix" className="h-9 px-3 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#6590FF]" />
          </div>
          <p className="text-[10px] text-[#6B7280]">Database must have <code>CREATE EXTENSION vector;</code> already run. Env overrides: <code>PGVECTOR_HOST/PORT/DB/USER/PASSWORD</code>.</p>
        </div>
      )}

      {/* Test + Save bar */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            {draftBackendInfo.needs_connection && (
              <button onClick={test} disabled={testing} className="h-9 px-4 border border-[#E5E7EB] text-[#4B5563] text-xs font-medium rounded-lg hover:bg-[#F3F4F6] flex items-center gap-1.5" data-testid="test-backend-btn">
                {testing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Activity className="w-3 h-3" />} Test Connection
              </button>
            )}
            {requiresMigration && (
              <label className="flex items-center gap-2 text-xs text-[#4B5563]">
                <input type="checkbox" checked={migrate} onChange={e => setMigrate(e.target.checked)} data-testid="migrate-checkbox" />
                Re-index existing data after switching
              </label>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => { setDraft(JSON.parse(JSON.stringify(data.config))); setMigrate(false); setTestResult(null); }}
              disabled={!isChanged} className="h-9 px-4 border border-[#E5E7EB] text-[#4B5563] text-xs font-medium rounded-lg hover:bg-[#F3F4F6] disabled:opacity-50">
              Reset
            </button>
            <button onClick={save} disabled={!isChanged || saving || !draftBackendInfo.enabled}
              className="h-9 px-5 bg-[#6590FF] text-white text-xs font-semibold rounded-lg disabled:bg-[#9CA3AF] flex items-center gap-2" data-testid="save-vector-config-btn">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />} Save Config
            </button>
          </div>
        </div>
        {testResult && (
          <div className={`mt-3 p-3 rounded-lg border text-[11px] ${testResult.ok ? 'bg-[#059669]/5 border-[#059669]/30 text-[#059669]' : 'bg-[#DC2626]/5 border-[#DC2626]/30 text-[#DC2626]'}`}>
            <div className="font-bold mb-1">{testResult.ok ? '✓ Success' : '✗ Failed'}</div>
            <pre className="text-[10px] whitespace-pre-wrap font-mono">{JSON.stringify(testResult, null, 2).slice(0, 800)}</pre>
          </div>
        )}
      </div>
    </div>
  );
}



/* ===== AI MODELS (LLMs) ===== */
function LLMsTab() {
  const [data, setData] = useState({ llms: [], providers: {} });
  const [allTenants, setAllTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const emptyForm = { label: '', provider: 'anthropic', model_name: '', via: 'emergent', kind: 'text', api_key: '', base_url: '', api_version: '', oci_config_text: '', oci_private_key: '', oci_compartment_id: '' };
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all' | 'text' | 'embedding'

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [llmRes, tenRes] = await Promise.all([
        superadminAPI.listLLMs(),
        superadminAPI.listAllTenantsForLLM().catch(() => ({ data: [] })),
      ]);
      setData(llmRes.data || { llms: [], providers: {} });
      setAllTenants(tenRes.data || []);
    } catch (e) { console.error('Failed to load AI Models:', e); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const startCreate = () => { setEditing(null); setForm(emptyForm); setShowForm(true); };
  const startEdit = (l) => {
    setEditing(l.id);
    setForm({
      label: l.label, provider: l.provider, model_name: l.model_name, via: l.via,
      kind: l.kind || 'text', api_key: '', base_url: l.base_url || '',
      api_version: l.api_version || '',
      oci_config_text: '',
      oci_private_key: '',
      oci_compartment_id: l.oci_config?.compartment_id || '',
    });
    setShowForm(true);
  };
  const cancelForm = () => { setShowForm(false); setEditing(null); setForm(emptyForm); };

  const save = async () => {
    if (!form.label.trim() || !form.provider || !form.model_name.trim()) return;
    // OCI: require config + private key (unless editing and user is keeping existing key) + compartment
    if (form.provider === 'oci') {
      if (!form.oci_config_text?.trim() && !editing) { alert('OCI config text is required'); return; }
      if (!editing && !form.oci_private_key?.trim()) { alert('OCI private key (PEM) is required'); return; }
      if (!form.oci_compartment_id?.trim()) { alert('Compartment OCID is required'); return; }
    }
    // Azure OpenAI: api_version + endpoint + api_key are all mandatory.
    if (form.provider === 'azure-openai') {
      if (!form.base_url?.trim()) { alert('Azure endpoint (Base URL) is required'); return; }
      if (!editing && !form.api_key?.trim()) { alert('Azure API key is required'); return; }
      if (!form.api_version?.trim()) { alert('Azure API version is required (e.g. 2025-01-01-preview)'); return; }
    }
    setSaving(true);
    try {
      const payload = { ...form };
      // Strip OCI fields if not OCI to keep payload clean
      if (form.provider !== 'oci') {
        delete payload.oci_config_text;
        delete payload.oci_private_key;
        delete payload.oci_compartment_id;
      }
      if (editing) await superadminAPI.updateLLM(editing, payload);
      else await superadminAPI.createLLM(payload);
      cancelForm();
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const toggle = async (id) => {
    try { await superadminAPI.toggleLLM(id); load(); }
    catch (e) { alert(e.response?.data?.detail || 'Toggle failed'); }
  };
  const setDefault = async (id) => {
    try { await superadminAPI.setDefaultLLM(id); load(); }
    catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this AI model?')) return;
    try { await superadminAPI.deleteLLM(id); load(); }
    catch (e) { alert(e.response?.data?.detail || 'Delete failed'); }
  };

  const providers = data.providers || {};
  const providerInfo = providers[form.provider];
  const providerModels = providerInfo?.models || [];
  const isCustom = form.via === 'custom' || form.via === 'byo';
  const isOCI = form.provider === 'oci';
  const filtered = data.llms.filter(l => filter === 'all' || (l.kind || 'text') === filter);

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="sa-llms">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-bold text-[#111827] mb-1">AI Models</h2>
          <p className="text-sm text-[#6B7280]">Manage which LLMs and embedding models are available platform-wide. Use built-in providers, bring your own (BYO) endpoint, or assign specific models to specific workspaces.</p>
        </div>
        {!showForm && (
          <button onClick={startCreate} className="h-9 px-4 bg-[#6590FF] text-white text-xs font-semibold rounded-lg flex items-center gap-2" data-testid="add-llm-btn">
            <Plus className="w-3.5 h-3.5" /> Add AI Model
          </button>
        )}
      </div>

      {/* Kind filter pills */}
      <div className="flex items-center gap-2">
        {[{id: 'all', label: 'All'}, {id: 'text', label: 'Text / Generation'}, {id: 'embedding', label: 'Embedding'}].map(p => (
          <button key={p.id} onClick={() => setFilter(p.id)}
            className={`h-7 px-3 text-[11px] font-semibold rounded-lg border transition-colors ${
              filter === p.id ? 'bg-[#6590FF] text-white border-[#6590FF]' : 'bg-white text-[#4B5563] border-[#E5E7EB] hover:bg-[#F3F4F6]'
            }`}>{p.label}</button>
        ))}
      </div>

      {showForm && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-5">
          <h3 className="text-sm font-bold text-[#111827] mb-4">{editing ? 'Edit' : 'New'} AI Model</h3>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} placeholder="Label (e.g. 'Claude Sonnet 4.5') *" className="h-9 px-3 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#6590FF]" data-testid="llm-label" />
            <select value={form.kind} onChange={e => setForm({ ...form, kind: e.target.value })} className="h-9 px-3 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#6590FF]" data-testid="llm-kind">
              <option value="text">Text / Generation</option>
              <option value="embedding">Embedding</option>
            </select>
            <select value={form.via} onChange={e => setForm({ ...form, via: e.target.value, api_key: '', base_url: '' })} className="h-9 px-3 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#6590FF]">
              <option value="emergent">Via BrainShift LLM Key</option>
              <option value="byo">Bring Your Own (custom endpoint + key)</option>
            </select>
            <select value={form.provider} onChange={e => {
              const newProv = e.target.value;
              setForm({ ...form, provider: newProv, model_name: '', via: newProv === 'oci' || newProv === 'azure-openai' ? 'byo' : (newProv === 'anthropic' || newProv === 'openai' || newProv === 'gemini' ? 'emergent' : form.via) });
            }} className="h-9 px-3 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#6590FF]" data-testid="llm-provider">
              {Object.entries(providers).map(([id, p]) => (
                <option key={id} value={id}>{p.label} ({id})</option>
              ))}
              <option value="huggingface">Hugging Face</option>
              <option value="ollama">Ollama (self-hosted)</option>
              <option value="azure-openai">Azure OpenAI</option>
              <option value="cohere">Cohere</option>
              <option value="custom">Custom Provider</option>
            </select>
            {providerModels.length > 0 && (form.via === 'emergent' || form.provider === 'oci') ? (
              <select value={form.model_name} onChange={e => setForm({ ...form, model_name: e.target.value })} className="h-9 px-3 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#6590FF]" data-testid="llm-model">
                <option value="">Select model...</option>
                {providerModels.map(m => <option key={m} value={m}>{m}</option>)}
                <option value="__custom__">Other (type below)...</option>
              </select>
            ) : null}
            {(providerModels.length === 0 || (form.via !== 'emergent' && form.provider !== 'oci') || form.model_name === '__custom__') && (
              <input value={form.model_name === '__custom__' ? '' : form.model_name} onChange={e => setForm({ ...form, model_name: e.target.value })} placeholder={form.kind === 'embedding' ? 'Model name (e.g. text-embedding-3-small)' : 'Model name (e.g. gpt-4o-mini)'} className="h-9 px-3 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#6590FF]" />
            )}
          </div>
          {isOCI && (
            <div className="border-t border-[#F3F4F6] pt-3" data-testid="oci-config-block">
              <label className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider">Oracle Cloud Generative AI — credentials</label>
              <p className="text-[10px] text-[#6B7280] mt-1 mb-3">OCI uses RSA-signed requests instead of API keys. Paste the contents of your <code className="px-1 bg-[#F3F4F6] rounded">~/.oci/config</code> file (with the <code className="px-1 bg-[#F3F4F6] rounded">[DEFAULT]</code> section), then your private key PEM, then the compartment OCID where inference will run.</p>
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className="text-[10px] font-semibold text-[#4B5563] block mb-1">OCI config file contents *</label>
                  <textarea value={form.oci_config_text || ''} onChange={e => setForm({ ...form, oci_config_text: e.target.value })}
                    placeholder={"[DEFAULT]\nuser=ocid1.user.oc1..xxxxxx\ntenancy=ocid1.tenancy.oc1..xxxxxx\nfingerprint=aa:bb:cc:dd:ee:ff:00:11:22:33:44:55:66:77:88:99\nregion=us-chicago-1\nkey_file=/Users/me/.oci/oci_api_key.pem"}
                    rows={6} className="w-full px-3 py-2 border border-[#E5E7EB] rounded-lg text-xs font-mono focus:outline-none focus:border-[#6590FF]" data-testid="oci-config-text" />
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-[#4B5563] block mb-1">Private key (PEM) {editing ? '' : '*'}</label>
                  <textarea value={form.oci_private_key || ''} onChange={e => setForm({ ...form, oci_private_key: e.target.value })}
                    placeholder={editing ? 'Leave blank to keep existing key' : '-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----'}
                    rows={5} className="w-full px-3 py-2 border border-[#E5E7EB] rounded-lg text-xs font-mono focus:outline-none focus:border-[#6590FF]" data-testid="oci-private-key" />
                </div>
                <input value={form.oci_compartment_id || ''} onChange={e => setForm({ ...form, oci_compartment_id: e.target.value })}
                  placeholder="Compartment OCID (ocid1.compartment.oc1...) *"
                  className="h-9 px-3 border border-[#E5E7EB] rounded-lg text-sm font-mono focus:outline-none focus:border-[#6590FF]" data-testid="oci-compartment-id" />
              </div>
              <p className="text-[10px] text-[#9CA3AF] mt-2">Private key is stored encrypted at rest. The compartment must have GenAI inference policies attached.</p>
            </div>
          )}
          {isCustom && !isOCI && (
            <div className="border-t border-[#F3F4F6] pt-3">
              <label className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider">Custom / BYO endpoint</label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <input value={form.base_url || ''} onChange={e => setForm({ ...form, base_url: e.target.value })} placeholder={form.provider === 'azure-openai' ? 'Endpoint (e.g. https://your-resource.openai.azure.com/)' : 'Base URL (e.g. https://api.openai.com/v1)'} className="h-9 px-3 border border-[#E5E7EB] rounded-lg text-sm font-mono focus:outline-none focus:border-[#6590FF]" />
                <input type="password" autoComplete="off" name="llm-api-key" data-1p-ignore="true" data-lpignore="true" value={form.api_key || ''} onChange={e => setForm({ ...form, api_key: e.target.value })} placeholder={editing ? 'Leave blank to keep existing key' : 'API Key *'} className="h-9 px-3 border border-[#E5E7EB] rounded-lg text-sm font-mono focus:outline-none focus:border-[#6590FF]" data-testid="llm-api-key" />
              </div>
              {form.provider === 'azure-openai' && (
                <div className="mt-3" data-testid="azure-api-version-block">
                  <label className="text-[10px] font-semibold text-[#4B5563] block mb-1">API Version <span className="text-[#DC2626]">*</span></label>
                  <input value={form.api_version || ''} onChange={e => setForm({ ...form, api_version: e.target.value })} placeholder="e.g. 2025-01-01-preview" className="w-full h-9 px-3 border border-[#E5E7EB] rounded-lg text-sm font-mono focus:outline-none focus:border-[#6590FF]" data-testid="azure-api-version" />
                  <p className="text-[10px] text-[#9CA3AF] mt-1">Azure requires <code>api_version</code> on every call. The <strong>Model ID</strong> above must match the Azure <strong>deployment name</strong> (e.g. <code>gpt-4.1</code>).</p>
                </div>
              )}
              <p className="text-[10px] text-[#6B7280] mt-2">Use BYO for: self-hosted Ollama, Azure OpenAI, Hugging Face Inference, custom OpenAI-compatible endpoints, etc.</p>
            </div>
          )}
          <div className="flex gap-2 justify-end mt-4">
            <button onClick={cancelForm} className="h-9 px-4 border border-[#E5E7EB] text-[#4B5563] text-xs font-medium rounded-lg hover:bg-[#F3F4F6]">Cancel</button>
            <button onClick={save} disabled={!form.label.trim() || !form.provider || !form.model_name.trim() || saving} className="h-9 px-5 bg-[#6590FF] text-white text-xs font-semibold rounded-lg disabled:bg-[#9CA3AF] flex items-center gap-2" data-testid="save-llm-btn">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} {editing ? 'Update' : 'Create'}
            </button>
          </div>
        </div>
      )}

      {/* List grouped by Default / Not Default */}
      {loading ? <Loader2 className="w-6 h-6 text-[#6590FF] animate-spin mx-auto mt-8" /> : filtered.length === 0 ? (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-12 text-center">
          <Cloud className="w-10 h-10 text-[#D1D5DB] mx-auto mb-3" />
          <p className="text-sm text-[#6B7280]">No AI models in this category. Add one above.</p>
        </div>
      ) : (() => {
        const defaults = filtered.filter(l => l.is_default);
        const others = filtered.filter(l => !l.is_default);
        const Section = ({ title, items, accent, hint }) => items.length === 0 ? null : (
          <div className="space-y-2" data-testid={`llm-group-${title.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="flex items-baseline gap-2 px-1 mt-1">
              <span className={`inline-block w-1.5 h-1.5 rounded-full ${accent}`} />
              <h3 className="text-[10px] font-bold text-[#111827] uppercase tracking-wider">{title}</h3>
              <span className="text-[10px] text-[#9CA3AF]">{items.length}</span>
              {hint && <span className="text-[10px] text-[#9CA3AF] ml-auto">{hint}</span>}
            </div>
            {items.map(l => (
              <LLMRow key={l.id} llm={l} allTenants={allTenants}
                onToggle={() => toggle(l.id)} onSetDefault={() => setDefault(l.id)}
                onEdit={() => startEdit(l)} onDelete={() => remove(l.id)}
                onTenantsChanged={load} />
            ))}
          </div>
        );
        return (
          <div className="space-y-5">
            <Section title="Default" items={defaults} accent="bg-[#059669]"
              hint="One default per kind — applied across the platform when no workspace override is set" />
            <Section title="Not Default" items={others} accent="bg-[#9CA3AF]" />
          </div>
        );
      })()}
    </div>
  );
}

function LLMRow({ llm, allTenants, onToggle, onSetDefault, onEdit, onDelete, onTenantsChanged }) {
  const [expanded, setExpanded] = useState(false);
  const [assigned, setAssigned] = useState([]);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');

  const loadAssignments = useCallback(async () => {
    try { const r = await superadminAPI.getLLMTenants(llm.id); setAssigned(r.data || []); }
    catch (e) { console.error(e); }
  }, [llm.id]);
  useEffect(() => { if (expanded) loadAssignments(); }, [expanded, loadAssignments]);

  const assign = async (companyId) => { setBusy(true); try { await superadminAPI.assignLLMTenant(llm.id, companyId); await loadAssignments(); onTenantsChanged && onTenantsChanged(); } catch (e) { console.error(e); } setBusy(false); };
  const unassign = async (companyId) => { setBusy(true); try { await superadminAPI.unassignLLMTenant(llm.id, companyId); await loadAssignments(); onTenantsChanged && onTenantsChanged(); } catch (e) { console.error(e); } setBusy(false); };

  const assignedIds = new Set(assigned.map(a => a.company_id));
  const isEmbedding = (llm.kind || 'text') === 'embedding';
  const isBYO = llm.via === 'custom';

  const searchLower = search.trim().toLowerCase();
  const filteredAvailable = allTenants.filter(t =>
    !assignedIds.has(t.id) &&
    (!searchLower ||
      t.name.toLowerCase().includes(searchLower) ||
      (t.owner_email || '').toLowerCase().includes(searchLower)));

  return (
    <div className={`bg-white border rounded-xl p-4 ${llm.is_default ? 'border-[#6590FF] ring-1 ring-[#6590FF]/30' : 'border-[#E5E7EB]'}`} data-testid={`llm-${llm.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#F3F4F6] flex items-center justify-center flex-shrink-0">
          <Cloud className={`w-5 h-5 ${isEmbedding ? 'text-[#7C3AED]' : 'text-[#6590FF]'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h4 className="text-sm font-semibold text-[#111827]">{llm.label}</h4>
            {llm.is_default && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-[#6590FF]/10 text-[#6590FF]">Default</span>}
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${isEmbedding ? 'bg-[#7C3AED]/10 text-[#7C3AED]' : 'bg-[#0891B2]/10 text-[#0891B2]'}`}>{isEmbedding ? 'Embedding' : 'Text'}</span>
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${llm.enabled ? 'bg-[#059669]/10 text-[#059669]' : 'bg-[#9CA3AF]/10 text-[#6B7280]'}`}>{llm.enabled ? 'Enabled' : 'Disabled'}</span>
            {isBYO && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-[#D97706]/10 text-[#D97706]">BYO</span>}
            {!isBYO && <span className="text-[10px] font-medium text-[#6B7280] bg-[#F3F4F6] px-2 py-0.5 rounded">BrainShift</span>}
          </div>
          <p className="text-[11px] text-[#6B7280] font-mono">
            {llm.provider} · {llm.model_name}{llm.base_url && ` · ${llm.base_url}`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
          {!llm.is_default && llm.enabled && !isEmbedding && (
            <button onClick={onSetDefault} className="h-7 px-2.5 border border-[#6590FF] text-[#6590FF] text-[10px] font-medium rounded-lg hover:bg-[#6590FF]/5" data-testid={`set-default-${llm.id}`}>Set Default</button>
          )}
          <button onClick={() => setExpanded(!expanded)} className="h-7 px-2.5 border border-[#E5E7EB] text-[#4B5563] text-[10px] font-medium rounded-lg hover:bg-[#F3F4F6] flex items-center gap-1" data-testid={`assign-tenants-${llm.id}`}>
            {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />} Assign Tenants{assigned.length > 0 ? ` (${assigned.length})` : ''}
          </button>
          <button onClick={onToggle} className="h-7 px-2.5 border border-[#E5E7EB] text-[#4B5563] text-[10px] font-medium rounded-lg hover:bg-[#F3F4F6] flex items-center gap-1" data-testid={`toggle-${llm.id}`}>
            {llm.enabled ? <ToggleRight className="w-3 h-3 text-[#059669]" /> : <ToggleLeft className="w-3 h-3 text-[#9CA3AF]" />} {llm.enabled ? 'Disable' : 'Enable'}
          </button>
          <button onClick={onEdit} className="h-7 px-2.5 border border-[#E5E7EB] text-[#4B5563] text-[10px] font-medium rounded-lg hover:bg-[#F3F4F6] flex items-center gap-1"><Pencil className="w-3 h-3" /></button>
          <button onClick={onDelete} disabled={llm.is_default} className="h-7 px-2.5 border border-[#FCA5A5] text-[#DC2626] text-[10px] font-medium rounded-lg hover:bg-[#FEE2E2] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"><Trash2 className="w-3 h-3" /></button>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-[#F3F4F6]">
          <h5 className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider mb-2">Tenant Access ({assigned.length})</h5>
          <p className="text-[10px] text-[#9CA3AF] mb-3">When you assign tenants, all workspaces in those tenants can use this AI model. If no tenant is assigned, this model is available to all tenants by default.</p>
          {assigned.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {assigned.map(t => (
                <span key={t.company_id} className="inline-flex items-center gap-1 text-[11px] font-medium text-[#6590FF] bg-[#6590FF]/5 px-2.5 py-1 rounded-lg border border-[#6590FF]/20">
                  {t.name}
                  <button disabled={busy} onClick={() => unassign(t.company_id)} className="hover:text-[#DC2626] disabled:opacity-50"><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          )}
          <div className="relative mb-3">
            <Search className="w-3.5 h-3.5 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder={`Search ${allTenants.length} tenants by name or owner email...`}
              className="h-9 pl-9 pr-3 w-full border border-[#E5E7EB] rounded-lg text-xs focus:outline-none focus:border-[#6590FF]"
              data-testid={`tenant-search-${llm.id}`} />
          </div>
          <div className="max-h-64 overflow-y-auto border border-[#E5E7EB] rounded-lg divide-y divide-[#F3F4F6]">
            {filteredAvailable.length === 0 ? (
              <p className="text-[10px] text-[#9CA3AF] text-center py-6">
                {search ? 'No tenants match your search.' : 'All tenants assigned.'}
              </p>
            ) : (
              filteredAvailable.slice(0, 50).map(t => (
                <button key={t.id} disabled={busy} onClick={() => assign(t.id)}
                  className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left hover:bg-[#F9FAFB] disabled:opacity-50">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-[#111827] truncate">{t.name}</div>
                    {t.owner_email && <div className="text-[10px] text-[#9CA3AF] truncate">{t.owner_email}</div>}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {t.workspace_count > 0 && <span className="text-[10px] text-[#6B7280]">{t.workspace_count} ws</span>}
                    <Plus className="w-3.5 h-3.5 text-[#6590FF]" />
                  </div>
                </button>
              ))
            )}
            {filteredAvailable.length > 50 && (
              <p className="text-[10px] text-[#9CA3AF] text-center py-2 bg-[#F9FAFB]">
                Showing 50 of {filteredAvailable.length}. Refine your search to see more.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Phase 3.5: SuperAdmin AI Usage tab (system-wide + quota management) ----
function SuperAdminUsageTab() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [limits, setLimits] = useState([]);
  const [showLimitForm, setShowLimitForm] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    Promise.all([
      aiUsageAPI.systemUsage(days, companyId || undefined),
      aiUsageAPI.listLimits(),
      superadminAPI.listAllTenantsForLLM().catch(() => ({ data: [] })),
    ]).then(([usage, lims, tens]) => {
      setData(usage.data || null);
      setLimits(Array.isArray(lims.data) ? lims.data : []);
      setTenants(Array.isArray(tens.data) ? tens.data : []);
    }).catch(console.error).finally(() => setLoading(false));
  }, [days, companyId]);
  useEffect(() => { reload(); }, [reload]);

  const fmt = (n) => Number(n || 0).toLocaleString();
  const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;
  const totals = data?.totals || {};
  const tenantName = (id) => tenants.find(t => t.id === id)?.name || id;

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#6590FF]" /></div>;

  return (
    <div className="space-y-5" data-testid="superadmin-usage-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-[#111827]" style={{ fontFamily: 'var(--font-heading)' }}>AI Usage & Cost Analytics</h2>
          <p className="text-xs text-[#6B7280] mt-0.5">Token consumption and quota enforcement across all tenants.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={companyId} onChange={e => setCompanyId(e.target.value)}
            className="h-8 px-2 text-xs border border-[#E5E7EB] rounded-lg bg-white" data-testid="usage-company-filter">
            <option value="">All companies</option>
            {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <select value={days} onChange={e => setDays(Number(e.target.value))}
            className="h-8 px-2 text-xs border border-[#E5E7EB] rounded-lg bg-white" data-testid="usage-period-filter">
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-3"><p className="text-[10px] font-bold uppercase text-[#9CA3AF] tracking-wider">Total tokens</p><p className="text-xl font-bold text-[#111827] mt-1">{fmt(totals.total_tokens)}</p></div>
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-3"><p className="text-[10px] font-bold uppercase text-[#9CA3AF] tracking-wider">Calls</p><p className="text-xl font-bold text-[#111827] mt-1">{fmt(totals.calls)}</p></div>
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-3"><p className="text-[10px] font-bold uppercase text-[#9CA3AF] tracking-wider">Errors</p><p className={`text-xl font-bold mt-1 ${totals.errors ? 'text-[#DC2626]' : 'text-[#111827]'}`}>{fmt(totals.errors)}</p></div>
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-3"><p className="text-[10px] font-bold uppercase text-[#9CA3AF] tracking-wider">Estimated cost</p><p className="text-xl font-bold text-[#6590FF] mt-1">{fmtMoney(totals.cost_usd)}</p></div>
      </div>

      {data?.daily?.length > 0 && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
          <h3 className="text-xs font-bold uppercase tracking-wider text-[#6B7280] mb-2">Daily token consumption</h3>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={data.daily}>
              <defs>
                <linearGradient id="saUsageGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6590FF" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#6590FF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#6B7280' }} />
              <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} />
              <RTooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v) => fmt(v)} />
              <Area type="monotone" dataKey="tokens" stroke="#6590FF" fill="url(#saUsageGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {data?.by_provider?.length > 0 && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
          <h3 className="text-xs font-bold uppercase tracking-wider text-[#6B7280] mb-3">Top models by token volume</h3>
          <ResponsiveContainer width="100%" height={Math.max(180, data.by_provider.length * 30)}>
            <BarChart data={data.by_provider} layout="vertical" margin={{ left: 90 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10, fill: '#6B7280' }} />
              <YAxis type="category" dataKey="model" tick={{ fontSize: 10, fill: '#374151' }} width={140} />
              <RTooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v) => fmt(v)} />
              <Bar dataKey="tokens" fill="#6590FF" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-[#111827]">Quota policies</h3>
          <button onClick={() => setShowLimitForm(true)}
            className="h-8 px-3 text-xs font-semibold text-white bg-[#6590FF] hover:bg-[#4B7CFF] rounded-xl flex items-center gap-1.5"
            data-testid="add-limit-btn">
            <Plus className="w-3.5 h-3.5" /> New limit
          </button>
        </div>
        {limits.length === 0 ? (
          <p className="text-xs text-[#9CA3AF] text-center py-6">No quota policies configured. Click "New limit" to enforce token or cost ceilings.</p>
        ) : (
          <div className="space-y-2">
            {limits.map(l => (
              <div key={l.id} className="flex items-center justify-between p-2 bg-[#F9FAFB] rounded-lg" data-testid={`limit-${l.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${l.mode === 'hard' ? 'bg-[#FEE2E2] text-[#DC2626]' : l.mode === 'throttle' ? 'bg-[#FEF3C7] text-[#92400E]' : 'bg-[#DBEAFE] text-[#1E40AF]'}`}>{l.mode}</span>
                    <span className="text-xs font-semibold text-[#111827]">{l.scope_type}</span>
                    {l.scope_type !== 'system' && <span className="text-[11px] text-[#6B7280] truncate">{l.scope_type === 'company' ? tenantName(l.scope_id) : l.scope_id?.slice(0, 8) + '…'}</span>}
                    <span className="text-[11px] text-[#6B7280]">/ {l.period}</span>
                  </div>
                  <p className="text-[11px] text-[#6B7280] mt-0.5">
                    {l.max_tokens ? `${fmt(l.max_tokens)} tokens` : ''}
                    {l.max_tokens && l.max_cost_usd ? ' · ' : ''}
                    {l.max_cost_usd ? `${fmtMoney(l.max_cost_usd)}` : ''}
                    {' · warn at '}{l.warn_at_pct}%
                  </p>
                </div>
                <button onClick={async () => { if (window.confirm('Delete this limit?')) { await aiUsageAPI.deleteLimit(l.id); reload(); } }}
                  className="p-1.5 hover:bg-[#FEE2E2] rounded-lg transition-colors" data-testid={`delete-limit-${l.id}`}>
                  <Trash2 className="w-3.5 h-3.5 text-[#DC2626]" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {showLimitForm && <LimitFormModal onClose={() => setShowLimitForm(false)} onSaved={() => { setShowLimitForm(false); reload(); }} tenants={tenants} />}

      {!data?.daily?.length && !data?.by_provider?.length && (
        <div className="bg-[#F9FAFB] border border-dashed border-[#E5E7EB] rounded-xl p-8 text-center">
          <BarChart3 className="w-8 h-8 text-[#D1D5DB] mx-auto mb-2" />
          <p className="text-sm text-[#6B7280]">No AI activity in the selected window.</p>
        </div>
      )}
    </div>
  );
}

function LimitFormModal({ onClose, onSaved, tenants }) {
  const [scopeType, setScopeType] = useState('system');
  const [scopeId, setScopeId] = useState('');
  // Multi-user selection state — used only when scopeType === 'user'.
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [period, setPeriod] = useState('month');
  const [maxTokens, setMaxTokens] = useState('');
  const [maxCost, setMaxCost] = useState('');
  const [mode, setMode] = useState('hard');
  const [warnAt, setWarnAt] = useState(80);
  const [saving, setSaving] = useState(false);

  // Fetch users lazily the first time scope='user' is selected.
  useEffect(() => {
    if (scopeType !== 'user' || users.length > 0 || usersLoading) return;
    setUsersLoading(true);
    setUsersError(null);
    superadminAPI.getUsers()
      .then(r => setUsers(Array.isArray(r.data) ? r.data : []))
      .catch(e => setUsersError(e.response?.data?.detail || 'Failed to load users'))
      .finally(() => setUsersLoading(false));
  }, [scopeType, users.length, usersLoading]);

  const filteredUsers = (() => {
    if (!userSearch.trim()) return users;
    const q = userSearch.toLowerCase();
    return users.filter(u =>
      (u.email || '').toLowerCase().includes(q) ||
      (u.first_name || '').toLowerCase().includes(q) ||
      (u.last_name || '').toLowerCase().includes(q) ||
      (u.company_name || '').toLowerCase().includes(q)
    );
  })();

  const toggleUser = (id) => {
    setSelectedUserIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const submit = async () => {
    if (scopeType === 'company' && !scopeId.trim()) { alert('Please select a tenant'); return; }
    if (scopeType === 'user' && selectedUserIds.length === 0) { alert('Please select at least one user'); return; }
    if (!maxTokens && !maxCost) { alert('Provide a token cap and/or cost cap'); return; }
    setSaving(true);
    try {
      const basePayload = {
        period,
        max_tokens: maxTokens ? Number(maxTokens) : null,
        max_cost_usd: maxCost ? Number(maxCost) : null,
        mode,
        warn_at_pct: Number(warnAt),
      };
      if (scopeType === 'user') {
        // Apply the same policy to every selected user — issue calls in parallel.
        // Use allSettled so we can surface partial failures rather than masking some successes
        // behind a generic "Save failed" alert.
        const results = await Promise.allSettled(selectedUserIds.map(uid =>
          aiUsageAPI.upsertLimit({ ...basePayload, scope_type: 'user', scope_id: uid })
        ));
        const failed = results.filter(r => r.status === 'rejected');
        if (failed.length > 0) {
          const succeeded = results.length - failed.length;
          const firstErr = failed[0].reason?.response?.data?.detail || failed[0].reason?.message || 'Unknown error';
          alert(`Saved ${succeeded} of ${results.length} policies. ${failed.length} failed: ${firstErr}`);
          if (succeeded > 0) onSaved();
          setSaving(false);
          return;
        }
      } else {
        await aiUsageAPI.upsertLimit({
          ...basePayload,
          scope_type: scopeType,
          scope_id: scopeType === 'system' ? null : scopeId.trim(),
        });
      }
      onSaved();
    } catch (e) { alert(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="limit-form-overlay">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
        <div className="h-12 border-b border-[#E5E7EB] flex items-center justify-between px-5 flex-shrink-0">
          <h3 className="text-sm font-bold text-[#111827]">New quota policy</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-[#F3F4F6] rounded-xl"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3 overflow-y-auto">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-[#6B7280]">Scope</label>
            <select value={scopeType} onChange={e => setScopeType(e.target.value)} className="w-full h-9 px-2 text-sm border border-[#E5E7EB] rounded-lg mt-1" data-testid="limit-scope-type">
              <option value="system">System (all tenants)</option>
              <option value="company">Company / tenant</option>
              <option value="user">User</option>
            </select>
          </div>
          {scopeType === 'company' && (
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-[#6B7280]">Tenant</label>
              <select value={scopeId} onChange={e => setScopeId(e.target.value)} className="w-full h-9 px-2 text-sm border border-[#E5E7EB] rounded-lg mt-1" data-testid="limit-scope-id">
                <option value="">— select —</option>
                {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          )}
          {scopeType === 'user' && (
            <UserMultiPicker
              users={filteredUsers}
              loading={usersLoading}
              error={usersError}
              search={userSearch}
              setSearch={setUserSearch}
              selectedIds={selectedUserIds}
              onToggle={toggleUser}
              onClearAll={() => setSelectedUserIds([])}
              totalUsers={users.length}
            />
          )}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-[#6B7280]">Period</label>
            <select value={period} onChange={e => setPeriod(e.target.value)} className="w-full h-9 px-2 text-sm border border-[#E5E7EB] rounded-lg mt-1" data-testid="limit-period">
              <option value="day">Day</option><option value="week">Week</option><option value="month">Month</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-[#6B7280]">Max tokens</label>
              <input type="number" min="0" value={maxTokens} onChange={e => setMaxTokens(e.target.value)} placeholder="e.g. 5000000" className="w-full h-9 px-2 text-sm border border-[#E5E7EB] rounded-lg mt-1" data-testid="limit-max-tokens" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-[#6B7280]">Max cost (USD)</label>
              <input type="number" min="0" step="0.01" value={maxCost} onChange={e => setMaxCost(e.target.value)} placeholder="e.g. 500.00" className="w-full h-9 px-2 text-sm border border-[#E5E7EB] rounded-lg mt-1" data-testid="limit-max-cost" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-[#6B7280]">Mode</label>
              <select value={mode} onChange={e => setMode(e.target.value)} className="w-full h-9 px-2 text-sm border border-[#E5E7EB] rounded-lg mt-1" data-testid="limit-mode">
                <option value="hard">Hard (block at 100%)</option>
                <option value="warn">Warn only</option>
                <option value="throttle">Throttle</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-[#6B7280]">Warn at %</label>
              <input type="number" min="1" max="100" value={warnAt} onChange={e => setWarnAt(e.target.value)} className="w-full h-9 px-2 text-sm border border-[#E5E7EB] rounded-lg mt-1" data-testid="limit-warn-pct" />
            </div>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-[#E5E7EB] flex items-center justify-between gap-2 flex-shrink-0">
          <span className="text-[11px] text-[#6B7280]" data-testid="limit-summary">
            {scopeType === 'user' && selectedUserIds.length > 0 && (
              <>{`Will create ${selectedUserIds.length} polic${selectedUserIds.length === 1 ? 'y' : 'ies'}`}</>
            )}
          </span>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="h-8 px-3 text-xs font-medium text-[#4B5563] hover:bg-[#F3F4F6] rounded-xl" data-testid="limit-cancel">Cancel</button>
            <button onClick={submit} disabled={saving || (scopeType === 'user' && selectedUserIds.length === 0)} className="h-8 px-3 text-xs font-semibold text-white bg-[#6590FF] hover:bg-[#4B7CFF] disabled:opacity-50 disabled:cursor-not-allowed rounded-xl flex items-center gap-1.5" data-testid="limit-save">
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save policy
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function UserMultiPicker({ users, loading, error, search, setSearch, selectedIds, onToggle, onClearAll, totalUsers }) {
  return (
    <div data-testid="limit-user-multi-picker">
      <div className="flex items-center justify-between mb-1">
        <label className="text-[10px] font-bold uppercase tracking-wider text-[#6B7280]">
          Users ({selectedIds.length} selected{totalUsers > 0 ? ` of ${totalUsers}` : ''})
        </label>
        {selectedIds.length > 0 && (
          <button type="button" onClick={onClearAll} className="text-[10px] font-medium text-[#6590FF] hover:underline" data-testid="limit-user-clear-all">
            Clear all
          </button>
        )}
      </div>
      <div className="relative mb-2">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#9CA3AF]" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, email, or tenant…"
          className="w-full h-9 pl-8 pr-3 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#6590FF]"
          data-testid="limit-user-search"
        />
      </div>
      <div className="border border-[#E5E7EB] rounded-lg max-h-56 overflow-y-auto bg-[#F9FAFB]" data-testid="limit-user-list">
        {loading && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-4 h-4 animate-spin text-[#6590FF]" />
          </div>
        )}
        {error && !loading && (
          <p className="text-xs text-[#DC2626] text-center py-4">{error}</p>
        )}
        {!loading && !error && users.length === 0 && (
          <p className="text-xs text-[#9CA3AF] text-center py-4">No matching users</p>
        )}
        {!loading && !error && users.map(u => {
          const checked = selectedIds.includes(u.id);
          const fullName = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;
          return (
            <button
              key={u.id}
              type="button"
              onClick={() => onToggle(u.id)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-white border-b border-[#E5E7EB] last:border-b-0 transition-colors ${checked ? 'bg-[#EEF2FF]' : ''}`}
              data-testid={`limit-user-row-${u.id}`}
            >
              <span
                className={`flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center ${
                  checked ? 'bg-[#6590FF] border-[#6590FF]' : 'bg-white border-[#D1D5DB]'
                }`}
                aria-hidden="true"
              >
                {checked && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-[#111827] truncate">
                  {fullName}
                  {u.is_super_admin && <span className="ml-1.5 text-[9px] font-bold text-[#7C3AED]">SUPER</span>}
                  {!u.active && <span className="ml-1.5 text-[9px] font-bold text-[#9CA3AF]">DISABLED</span>}
                </p>
                <p className="text-[10.5px] text-[#6B7280] truncate">
                  {u.email}{u.company_name ? ` · ${u.company_name}` : ''}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

